import { useEffect, useMemo, useState } from "react";
import api from "../api/axios";
import { API_ORIGIN } from "../api/apiConfig";
import AdminLoadingOverlay from "../components/AdminLoadingOverlay";
import AdminShell from "../components/AdminShell";
import { useAuth } from "../context/AuthContext";
import "../styles/admin.css";

const defaultSettings = {
  hero: {
    title: "Browse a polished fleet built for real reservations",
    subtitle:
      "Explore executive sedans, spacious SUVs, and versatile vans in one premium booking experience.",
    image: "/images/hero-car.png",
    ctaText: "Plan Another Trip",
    chips: ["Curated premium inventory", "Clear daily pricing", "Refined booking journey"],
    infoCards: [
      { title: "Ready to book", description: "2 premium units" },
      { title: "Trip-matched", description: "Browse all classes" },
      { title: "Transparent pricing", description: "Daily rate and deposit preview" },
    ],
  },
  filters: {
    categories: ["Sedan", "SUV", "Van", "MPV"],
    transmissions: ["Automatic", "Manual"],
    fuels: ["Gasoline", "Diesel", "Hybrid"],
    seatRanges: ["2 - 4", "5 - 7", "8 - 12+"],
    features: ["Air Conditioning", "GPS Navigation", "Child Seat", "Bluetooth"],
    featureCatalog: [
      { label: "Air Conditioning", category: "Comfort", priority: "high", visible: true },
      { label: "Bluetooth", category: "Convenience", priority: "high", visible: true },
      { label: "GPS Navigation", category: "Convenience", priority: "medium", visible: true },
      { label: "Child Seat", category: "Safety", priority: "medium", visible: true },
    ],
    behavior: {
      showPriceRange: true,
      showFuelType: true,
      showFeatures: true,
      defaultSort: "Recommended",
      defaultOpenSections: "Vehicle Categories, Price",
      maxSelectedFilters: "5",
    },
    pricePresets: [
      { label: "Budget", min: "0", max: "1999" },
      { label: "Standard", min: "2000", max: "4999" },
      { label: "Premium", min: "5000", max: "9999" },
      { label: "Luxury", min: "10000", max: "" },
    ],
    priceRange: {
      minimumPrice: 0,
      maximumPrice: 15000,
      priceStep: 500,
    },
  },
  general: {
    systemName: "FleetX",
    email: "support@fleetx.com",
    phone: "",
    address: "Unit 12, 3rd Floor, Trade Center Building, Makati City, Metro Manila, Philippines",
    footerDescription:
      "A modern car rental and fleet management platform built for secure, verified, and hassle-free vehicle reservations.",
    logo: "/logo.png",
    logoPublicId: "",
    websiteUrl: "https://www.fleetx.com",
    socialLinks: [
      { label: "Facebook", url: "https://facebook.com/fleetx" },
      { label: "Instagram", url: "https://instagram.com/fleetx" },
      { label: "LinkedIn", url: "https://linkedin.com/company/fleetx" },
    ],
    preferences: {
      currency: "PHP - Philippine Peso (P)",
      timezone: "(GMT+08:00) Asia/Manila",
      bookingPrefix: "FTX-",
      depositType: "Percentage (%)",
      pagination: "10 per page",
    },
    controls: {
      emailNotifications: true,
      otpRequired: true,
      maintenanceMode: false,
      sessionTimeout: "30 minutes",
    },
  },
  bookingRules: {
    tripPreferences: {
      maxPlanTripPassengers: 12,
      maxPlanTripLuggageBags: 6,
      maxPlanTripLuggageWeightKg: 100,
      heavyLuggageThresholdKg: 40,
      heavyLuggageWarningText: "Heavy luggage detected. Please double-check if it fits in this vehicle.",
      vehicleLuggageOverLimitText: "Luggage exceeds this vehicle's capacity. Please check the total weight.",
      useLuggageInRecommendations: true,
      vehicleLuggageLimits: {
        sedan:     { soft: 40,  hard: 60  },
        hatchback: { soft: 35,  hard: 55  },
        mpv:       { soft: 80,  hard: 120 },
        suv:       { soft: 100, hard: 150 },
        van:       { soft: 180, hard: 250 },
        pickup:    { soft: 200, hard: 300 },
        default:   { soft: 70,  hard: 100 },
      },
      vehicleLuggageBagLimits: {
        sedan:     { small: 5,  medium: 4,  large: 3, extraLarge: 2 },
        hatchback: { small: 4,  medium: 3,  large: 2, extraLarge: 1 },
        mpv:       { small: 7,  medium: 5,  large: 4, extraLarge: 3 },
        suv:       { small: 7,  medium: 5,  large: 4, extraLarge: 3 },
        van:       { small: 12, medium: 10, large: 8, extraLarge: 5 },
        pickup:    { small: 10, medium: 8,  large: 6, extraLarge: 4 },
        default:   { small: 4,  medium: 3,  large: 2, extraLarge: 1 },
      },
    },
    extraCharges: {
      deliveryFeeMode: "manual_review",
      fixedDeliveryFee: 0,
      baseDeliveryFee: 0,
      perKmDeliveryFee: 0,
      tollFeePolicy: "customer_responsibility",
      ferryBargeFeePolicy: "customer_responsibility",
      fuelPolicy: "customer_responsibility",
      otherChargeNotes: "",
      status: "active",
    },
  },
  contractTemplate: {
    title: "FleetX Vehicle Rental Agreement",
    companyName: "FleetX Transport Services",
    intro:
      'This Vehicle Rental Agreement is entered into by and between FleetX Transport Services, referred to as the "Company," and {{customerName}}, referred to as the "Renter," for booking {{bookingReference}}.',
    footerText:
      "By accepting this agreement electronically, the Renter confirms that the full agreement has been reviewed, the rental and payment terms are understood, and the Renter agrees to comply with FleetX Transport Services policies and the obligations stated in this Vehicle Rental Agreement.",
    versionLabel: "v1.0",
    effectiveDate: "2026-01-01",
    sections: [
      { title: "Parties to the Agreement", body: 'This Vehicle Rental Agreement is entered into by and between FleetX Transport Services, referred to as the "Company," and {{customerName}}, referred to as the "Renter." The Renter confirms that the booking information shown in this agreement is accurate and forms part of the rental record for reservation {{bookingReference}}.', sortOrder: 1, enabled: true },
      { title: "Vehicle and Rental Details", body: "Renter: {{customerName}}\nBooking Reference: {{bookingReference}}\nVehicle: {{vehicleName}}\nPlate Number: {{plateNumber}}\nRental Type: {{rentalType}}\nPickup Location: {{pickupLocation}}\nDestination / Drop-off: {{destination}}\nStart Date: {{startDate}}\nEnd Date: {{endDate}}", sortOrder: 2, enabled: true },
      { title: "Rental Fees and Payment Terms", body: "The Renter agrees to pay the rental charges, invoice amount, and any approved additional charges connected with this booking. Total rental amount: {{totalAmount}}. Amount to pay: {{amountToPay}}. Payment method: {{paymentMethod}} via {{paymentProvider}}. Invoice reference: {{invoiceReference}}.", sortOrder: 3, enabled: true },
      { title: "Renter Responsibilities", body: "The Renter confirms that all booking details provided are accurate and agrees to comply with the rental schedule, trip details, FleetX policies, and all approved payment obligations.", sortOrder: 4, enabled: true },
      { title: "Driver, Fuel, Toll, Parking, and Other Charges", body: "For bookings with driver service, the Renter acknowledges that driver assignment may occur before the trip and may be updated by the Company as needed. Driver meals or accommodation, tolls, parking, ferry fees, fuel adjustments, route changes, waiting time, and other verified trip-related charges may be billed when applicable.", sortOrder: 5, enabled: true },
      { title: "Late Return and Extension Policy", body: "Any request to extend the rental period must be submitted before the scheduled return time whenever possible. Extensions are subject to FleetX approval, vehicle availability, schedule feasibility, and applicable additional rental or service charges.", sortOrder: 6, enabled: true },
      { title: "Damage, Cleaning, Incident, and Penalty Responsibility", body: "The Renter must report accidents, incidents, vehicle concerns, delays, or damage immediately to the Company. Verified costs for vehicle damage, excessive cleaning, traffic or parking penalties, lost items, misuse, or other trip-related costs may be charged to the Renter after Company review.", sortOrder: 7, enabled: true },
      { title: "Cancellation and Refund Policy", body: "Cancellation and refund requests are subject to FleetX review and may depend on booking status, payment status, request timing, vehicle preparation, driver assignment, and operational costs already incurred for the booking.", sortOrder: 8, enabled: true },
      { title: "Payment Proof and Verification", body: "After sending payment, the Renter must upload a clear and valid payment proof showing the amount, date, transaction reference, and payment source when available. FleetX will review the submitted proof and may request a replacement if the proof cannot be verified.", sortOrder: 9, enabled: true },
      { title: "Acknowledgement and Electronic Signature", body: "By accepting this agreement electronically, the Renter confirms that the full agreement has been reviewed, the rental and payment terms are understood, and the Renter agrees to comply with FleetX Transport Services policies.", sortOrder: 10, enabled: true },
    ],
  },
};

const tabs = [
  { id: "browse", label: "Customer Browse Page" },
  { id: "filters", label: "Vehicle Filters" },
  { id: "bookingRules", label: "Booking Rules" },
  { id: "contracts", label: "Contract Templates" },
  { id: "general", label: "General" },
  { id: "payments", label: "Payment Methods" },
];

const FEATURE_CATEGORIES = ["Comfort", "Safety", "Convenience"];
const FEATURE_PRIORITIES = ["high", "medium", "low"];
const DEFAULT_PAYMENT_ACCOUNT_NAME = "FleetX Transport Services";
const PAYMENT_CATEGORIES = [
  "E-Wallet",
  "Online Banking / Bank Transfer",
  "Bank Deposit",
  "Office Payment",
];
const QR_ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const QR_MAX_SIZE_BYTES = 1 * 1024 * 1024;
const HERO_ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const HERO_MAX_SIZE_BYTES = 5 * 1024 * 1024;
const LOGO_ALLOWED_TYPES = HERO_ALLOWED_TYPES;
const LOGO_MAX_SIZE_BYTES = 2 * 1024 * 1024;
const CONTRACT_PLACEHOLDERS = [
  "customerName",
  "bookingReference",
  "invoiceReference",
  "vehicleName",
  "plateNumber",
  "rentalType",
  "pickupLocation",
  "destination",
  "startDate",
  "endDate",
  "totalAmount",
  "amountToPay",
  "paymentMethod",
  "paymentProvider",
  "acceptedAt",
  "typedSignature",
];
const CONTRACT_SAMPLE_VALUES = {
  customerName: "John Martin",
  bookingReference: "FX-2026-104582",
  invoiceReference: "INV-2026-0091",
  vehicleName: "Toyota Hiace 2024",
  plateNumber: "NAA 1234",
  rentalType: "With Driver",
  pickupLocation: "Makati City",
  destination: "Baguio, Benguet",
  startDate: "May 20, 2026",
  endDate: "May 22, 2026",
  totalAmount: "PHP 18,000.00",
  amountToPay: "PHP 5,400.00",
  paymentMethod: "Bank Deposit",
  paymentProvider: "China Banking Corporation",
  acceptedAt: "May 15, 2026, 2:30 PM",
  typedSignature: "John Martin",
};

const initialPaymentForm = {
  name: "",
  category: "E-Wallet",
  accountName: DEFAULT_PAYMENT_ACCOUNT_NAME,
  accountNumber: "",
  instructions: "",
  isActive: true,
};

function normalizePaymentCategoryLabel(category) {
  return category === "Bank Transfer"
    ? "Online Banking / Bank Transfer"
    : category || "E-Wallet";
}
const RESTRICTED_AREA_TARGETS = [
  { value: "pickup", label: "Pickup" },
  { value: "destination", label: "Destination" },
  { value: "dropoff", label: "Drop-off" },
  { value: "delivery", label: "Delivery address" },
];
const RESTRICTED_AREA_TARGET_OPTIONS = [
  { value: "pickup", label: "Pickup", targets: ["pickup"] },
  {
    value: "destination_dropoff",
    label: "Destination / Drop-off",
    targets: ["destination", "dropoff"],
  },
  { value: "delivery", label: "Delivery address", targets: ["delivery"] },
];
const DEFAULT_RESTRICTED_AREA_MESSAGE =
  "FleetX currently does not support bookings to this area. Please choose another destination or contact FleetX for assistance.";
const initialRestrictedAreaForm = {
  areaName: "",
  keywords: "",
  appliesTo: ["destination", "dropoff"],
  status: "active",
  message: DEFAULT_RESTRICTED_AREA_MESSAGE,
};
const PENALTY_TYPE_OPTIONS = [
  { value: "fixed_per_day", label: "Fixed amount per late day" },
  { value: "percentage_of_daily_rate", label: "Percentage of daily rate" },
  { value: "multiplier_of_daily_rate", label: "Multiplier of daily rate" },
];
const DELIVERY_FEE_MODE_OPTIONS = [
  { value: "fixed", label: "Fixed" },
  { value: "per_km", label: "Per kilometer" },
  { value: "manual_review", label: "Manual review" },
];
const EXTRA_CHARGE_POLICY_OPTIONS = [
  { value: "included", label: "Included" },
  { value: "customer_responsibility", label: "Customer responsibility" },
  { value: "manual_review", label: "Manual review" },
];
const FUEL_POLICY_OPTIONS = [
  { value: "customer_responsibility", label: "Customer responsibility" },
  { value: "full_to_full", label: "Full-to-full" },
  { value: "manual_review", label: "Manual review" },
];
const defaultPenaltyRule = {
  gracePeriodHours: "1",
  penaltyType: "percentage_of_daily_rate",
  penaltyValue: "50",
  applyWhenNoApprovedExtension: true,
  allowManualOverride: true,
  requireOverrideRemarks: true,
  status: "active",
};

function inferFeatureCategory(label) {
  const value = String(label || "").toLowerCase();
  if (/(air|seat|leather|climate|space|legroom|entertainment)/.test(value)) return "Comfort";
  if (/(airbag|brake|abs|camera|sensor|child|safety|belt|alarm)/.test(value)) return "Safety";
  return "Convenience";
}

function inferFeaturePriority(label) {
  const value = String(label || "").toLowerCase();
  if (/(air conditioning|airbag|abs|bluetooth|gps|navigation|camera)/.test(value)) {
    return "high";
  }
  if (/(child|sensor|usb|charger|seat|leather)/.test(value)) return "medium";
  return "low";
}

function normalizeFeatureCatalog(featureCatalog = [], fallbackFeatures = []) {
  const source = featureCatalog.length ? featureCatalog : fallbackFeatures;
  const seen = new Set();

  return source
    .map((item) => {
      const label = String(typeof item === "string" ? item : item?.label || "").trim();
      const category = FEATURE_CATEGORIES.includes(item?.category)
        ? item.category
        : inferFeatureCategory(label);
      const priority = FEATURE_PRIORITIES.includes(item?.priority)
        ? item.priority
        : inferFeaturePriority(label);

      return {
        label,
        category,
        priority,
        visible: item?.visible !== false,
      };
    })
    .filter((item) => {
      if (!item.label) return false;
      const key = item.label.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function normalizeContractTemplate(template = {}, fallback = defaultSettings.contractTemplate) {
  const source = template && typeof template === "object" ? template : {};
  const base = fallback && typeof fallback === "object" ? fallback : defaultSettings.contractTemplate;
  const sourceSections = Array.isArray(source.sections) ? source.sections : base.sections;
  const sections = sourceSections
    .map((section, index) => ({
      title: String(section?.title || "").trim(),
      body: String(section?.body || "").trim(),
      sortOrder: Number.isFinite(Number(section?.sortOrder)) ? Number(section.sortOrder) : index + 1,
      enabled: section?.enabled !== false,
    }))
    .filter((section) => section.title || section.body)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((section, index) => ({ ...section, sortOrder: index + 1 }));

  return {
    title: String(source.title ?? base.title ?? "").trim(),
    companyName: String(source.companyName ?? base.companyName ?? "").trim(),
    intro: String(source.intro ?? base.intro ?? "").trim(),
    footerText: String(source.footerText ?? base.footerText ?? "").trim(),
    versionLabel: String(source.versionLabel ?? base.versionLabel ?? "").trim(),
    effectiveDate: String(source.effectiveDate ?? base.effectiveDate ?? "").trim(),
    sections,
  };
}

function renderContractTemplateText(text, values = CONTRACT_SAMPLE_VALUES) {
  return String(text || "").replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_match, key) => {
    const value = values[key];
    return value === undefined || value === null || value === "" ? "N/A" : String(value);
  });
}

function renderContractTemplatePreview(template) {
  const normalized = normalizeContractTemplate(template);
  return {
    ...normalized,
    title: renderContractTemplateText(normalized.title),
    companyName: renderContractTemplateText(normalized.companyName),
    intro: renderContractTemplateText(normalized.intro),
    footerText: renderContractTemplateText(normalized.footerText),
    sections: normalized.sections
      .filter((section) => section.enabled !== false)
      .map((section, index) => ({
        ...section,
        title: renderContractTemplateText(section.title),
        body: renderContractTemplateText(section.body),
        sortOrder: index + 1,
      })),
  };
}

function contractParagraphs(text) {
  return String(text || "")
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function normalizeSettings(settings) {
  return {
    ...defaultSettings,
    ...settings,
    hero: {
      ...defaultSettings.hero,
      ...(settings?.hero || {}),
      chips: Array.isArray(settings?.hero?.chips)
        ? settings.hero.chips
        : defaultSettings.hero.chips,
      infoCards: (settings?.hero?.infoCards?.length
        ? settings.hero.infoCards
        : defaultSettings.hero.infoCards
      ).slice(0, 8),
    },
    filters: {
      ...defaultSettings.filters,
      ...(settings?.filters || {}),
      featureCatalog: normalizeFeatureCatalog(
        settings?.filters?.featureCatalog || [],
        settings?.filters?.features || defaultSettings.filters.features
      ),
      behavior: {
        ...defaultSettings.filters.behavior,
        ...(settings?.filters?.behavior || {}),
      },
      pricePresets: settings?.filters?.pricePresets?.length
        ? settings.filters.pricePresets
        : defaultSettings.filters.pricePresets,
      priceRange: {
        minimumPrice: Number(settings?.filters?.priceRange?.minimumPrice ?? 0),
        maximumPrice: Number(settings?.filters?.priceRange?.maximumPrice ?? 15000),
        priceStep: Number(settings?.filters?.priceRange?.priceStep ?? 500),
      },
    },
    general: {
      ...defaultSettings.general,
      ...(settings?.general || {}),
      socialLinks: settings?.general?.socialLinks?.length
        ? settings.general.socialLinks
        : defaultSettings.general.socialLinks,
      preferences: {
        ...defaultSettings.general.preferences,
        ...(settings?.general?.preferences || {}),
      },
      controls: {
        ...defaultSettings.general.controls,
        ...(settings?.general?.controls || {}),
      },
    },
    bookingRules: {
      ...defaultSettings.bookingRules,
      ...(settings?.bookingRules || {}),
      tripPreferences: {
        ...defaultSettings.bookingRules.tripPreferences,
        ...(settings?.bookingRules?.tripPreferences || {}),
        heavyLuggageWarningText:
          String(settings?.bookingRules?.tripPreferences?.heavyLuggageWarningText || "").trim() ||
          defaultSettings.bookingRules.tripPreferences.heavyLuggageWarningText,
        vehicleLuggageOverLimitText:
          String(settings?.bookingRules?.tripPreferences?.vehicleLuggageOverLimitText || "").trim() ||
          defaultSettings.bookingRules.tripPreferences.vehicleLuggageOverLimitText,
        vehicleLuggageLimits: (() => {
          const stored = settings?.bookingRules?.tripPreferences?.vehicleLuggageLimits || {};
          const def = defaultSettings.bookingRules.tripPreferences.vehicleLuggageLimits;
          const result = {};
          for (const cat of Object.keys(def)) {
            result[cat] = {
              soft: Number(stored[cat]?.soft) || def[cat].soft,
              hard: Number(stored[cat]?.hard) || def[cat].hard,
            };
          }
          return result;
        })(),
        vehicleLuggageBagLimits: (() => {
          const stored = settings?.bookingRules?.tripPreferences?.vehicleLuggageBagLimits || {};
          const def = defaultSettings.bookingRules.tripPreferences.vehicleLuggageBagLimits;
          const sizeKeys = ["small", "medium", "large", "extraLarge"];
          const result = {};
          for (const cat of Object.keys(def)) {
            result[cat] = {};
            for (const sizeKey of sizeKeys) {
              const n = Number(stored[cat]?.[sizeKey]);
              result[cat][sizeKey] = Number.isFinite(n) && n >= 0 ? n : def[cat][sizeKey];
            }
          }
          return result;
        })(),
      },
      extraCharges: {
        ...defaultSettings.bookingRules.extraCharges,
        ...(settings?.bookingRules?.extraCharges || {}),
      },
    },
    contractTemplate: normalizeContractTemplate(settings?.contractTemplate),
  };
}

function getImageSrc(path) {
  if (!path) return "";
  if (path.startsWith("blob:") || path.startsWith("http")) return path;
  if (path.startsWith("/uploads/")) return `${API_ORIGIN}${path}`;
  return path;
}

function validateQrFile(file) {
  if (!file) return "";
  if (!QR_ALLOWED_TYPES.includes(String(file.type || "").toLowerCase())) {
    return "QR code must be a JPG, PNG, or WEBP image.";
  }
  if (file.size > QR_MAX_SIZE_BYTES) {
    return "QR code must be 1MB or smaller.";
  }
  return "";
}

function validateHeroFile(file) {
  if (!file) return "";
  if (!HERO_ALLOWED_TYPES.includes(String(file.type || "").toLowerCase())) {
    return "Hero image must be a JPG, PNG, or WEBP image.";
  }
  if (file.size > HERO_MAX_SIZE_BYTES) {
    return "Hero image must be 5MB or smaller.";
  }
  return "";
}

function validateLogoFile(file) {
  if (!file) return "";
  if (!LOGO_ALLOWED_TYPES.includes(String(file.type || "").toLowerCase())) {
    return "Logo must be a JPG, PNG, or WEBP image.";
  }
  if (file.size > LOGO_MAX_SIZE_BYTES) {
    return "Logo must be 2MB or smaller.";
  }
  return "";
}

function splitKeywords(value) {
  const seen = new Set();
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter((item) => {
      const key = item.toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function formatRestrictedTargets(targets = []) {
  const labels = new Map(RESTRICTED_AREA_TARGETS.map((target) => [target.value, target.label]));
  return targets.map((target) => labels.get(target) || target).join(", ");
}

function formatPenaltyTypeLabel(value) {
  return PENALTY_TYPE_OPTIONS.find((option) => option.value === value)?.label || value;
}

function ruleToPenaltyForm(rule = {}) {
  return {
    gracePeriodHours: String(rule?.gracePeriodHours ?? defaultPenaltyRule.gracePeriodHours),
    penaltyType: rule?.penaltyType || defaultPenaltyRule.penaltyType,
    penaltyValue: String(rule?.penaltyValue ?? defaultPenaltyRule.penaltyValue),
    applyWhenNoApprovedExtension:
      rule?.applyWhenNoApprovedExtension ?? defaultPenaltyRule.applyWhenNoApprovedExtension,
    allowManualOverride: rule?.allowManualOverride ?? defaultPenaltyRule.allowManualOverride,
    requireOverrideRemarks: rule?.requireOverrideRemarks ?? defaultPenaltyRule.requireOverrideRemarks,
    status: rule?.status || defaultPenaltyRule.status,
  };
}

function settingsSignature(settings, hasImageFile) {
  return JSON.stringify({ settings, hasImageFile });
}

function SettingsSkeleton() {
  return (
    <div className="settings-skeleton">
      <div />
      <div />
      <div />
      <div />
    </div>
  );
}

function Field({ label, helper, children }) {
  return (
    <label className="settings-field">
      <span>{label}</span>
      {children}
      {helper ? <small>{helper}</small> : null}
    </label>
  );
}

function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      className={`settings-toggle ${checked ? "is-on" : ""}`}
      onClick={() => onChange(!checked)}
      aria-pressed={checked}
    >
      <span />
    </button>
  );
}

function ChipEditor({ title, values, onChange, placeholder = "Add item", removable = true }) {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");

  const addItem = () => {
    const next = draft.trim();
    if (!next) {
      setError("Enter a value first.");
      return;
    }
    if (values.some((value) => value.toLowerCase() === next.toLowerCase())) {
      setError("This item already exists.");
      return;
    }
    onChange([...values, next]);
    setDraft("");
    setError("");
  };

  return (
    <div className="settings-chip-section">
      {title ? <h3>{title}</h3> : null}
      <div className="settings-chip-list">
        {values.map((item) => (
          <span className="settings-chip" key={item}>
            {item}
            {removable ? (
              <button
                type="button"
                onClick={() => onChange(values.filter((value) => value !== item))}
                aria-label={`Remove ${item}`}
              >
                x
              </button>
            ) : null}
          </span>
        ))}
      </div>
      <div className="settings-add-row">
        <input
          className="settings-input"
          value={draft}
          placeholder={placeholder}
          onChange={(event) => {
            setDraft(event.target.value);
            setError("");
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              addItem();
            }
          }}
        />
        <button type="button" className="settings-secondary-btn" onClick={addItem}>
          Add
        </button>
      </div>
      {error ? <div className="settings-inline-error">{error}</div> : null}
    </div>
  );
}

function FeatureChipsEditor({ values, onChange }) {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");

  const addChip = () => {
    const next = draft.trim();
    if (!next) {
      setError("Enter a feature chip first.");
      return;
    }
    if (values.some((value) => value.trim().toLowerCase() === next.toLowerCase())) {
      setError("This feature chip already exists.");
      return;
    }
    onChange([...values, next]);
    setDraft("");
    setError("");
  };

  const updateChip = (index, value) => {
    onChange(values.map((item, itemIndex) => (itemIndex === index ? value : item)));
    setError("");
  };

  const finishEditingChip = (index) => {
    const next = String(values[index] || "").trim();
    if (!next) {
      onChange(values.filter((_, itemIndex) => itemIndex !== index));
      return;
    }
    const duplicateIndex = values.findIndex(
      (value, itemIndex) =>
        itemIndex !== index && value.trim().toLowerCase() === next.toLowerCase()
    );
    if (duplicateIndex >= 0) {
      setError("Feature chip text must be unique.");
      return;
    }
    updateChip(index, next);
  };

  return (
    <div className="settings-feature-chip-editor">
      <div className="settings-feature-chip-editor__list">
        {values.map((chip, index) => (
          <div className="settings-feature-chip-editor__row" key={`feature-chip-${index}`}>
            <input
              className="settings-input"
              value={chip}
              maxLength={80}
              aria-label={`Feature chip ${index + 1}`}
              onChange={(event) => updateChip(index, event.target.value)}
              onBlur={() => finishEditingChip(index)}
            />
            <button
              type="button"
              className="settings-icon-btn"
              aria-label={`Remove feature chip ${index + 1}`}
              onClick={() => onChange(values.filter((_, itemIndex) => itemIndex !== index))}
            >
              x
            </button>
          </div>
        ))}
      </div>
      <div className="settings-add-row">
        <input
          className="settings-input"
          value={draft}
          maxLength={80}
          placeholder="Add a feature chip..."
          onChange={(event) => {
            setDraft(event.target.value);
            setError("");
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              addChip();
            }
          }}
        />
        <button type="button" className="settings-secondary-btn" onClick={addChip}>
          Add
        </button>
      </div>
      {error ? <div className="settings-inline-error">{error}</div> : null}
    </div>
  );
}

function FeatureManager({ values, onChange }) {
  const [draft, setDraft] = useState({
    label: "",
    category: "Convenience",
    priority: "medium",
    visible: true,
  });

  const addFeature = () => {
    const label = draft.label.trim();
    if (!label) return;
    if (values.some((item) => item.label.toLowerCase() === label.toLowerCase())) return;
    onChange([...values, { ...draft, label }]);
    setDraft({ label: "", category: "Convenience", priority: "medium", visible: true });
  };

  const updateFeature = (index, key, value) => {
    onChange(values.map((item, itemIndex) => (
      itemIndex === index ? { ...item, [key]: value } : item
    )));
  };

  const removeFeature = (index) => {
    onChange(values.filter((_, itemIndex) => itemIndex !== index));
  };

  return (
    <div className="settings-feature-manager">
      <div className="settings-feature-manager__header">
        <span>Feature</span>
        <span>Category</span>
        <span>Priority</span>
        <span>Visible</span>
        <span />
      </div>

      {values.map((feature, index) => (
        <div className="settings-feature-row" key={`${feature.label}-${index}`}>
          <input
            className="settings-input"
            value={feature.label}
            onChange={(event) => updateFeature(index, "label", event.target.value)}
          />
          <select
            className="settings-input"
            value={feature.category}
            onChange={(event) => updateFeature(index, "category", event.target.value)}
          >
            {FEATURE_CATEGORIES.map((category) => (
              <option key={category}>{category}</option>
            ))}
          </select>
          <select
            className="settings-input"
            value={feature.priority}
            onChange={(event) => updateFeature(index, "priority", event.target.value)}
          >
            {FEATURE_PRIORITIES.map((priority) => (
              <option key={priority}>{priority}</option>
            ))}
          </select>
          <Toggle
            checked={feature.visible}
            onChange={(value) => updateFeature(index, "visible", value)}
          />
          <button type="button" className="settings-icon-btn" onClick={() => removeFeature(index)}>
            x
          </button>
        </div>
      ))}

      <div className="settings-feature-row settings-feature-row--add">
        <input
          className="settings-input"
          placeholder="Add new feature"
          value={draft.label}
          onChange={(event) => setDraft((prev) => ({ ...prev, label: event.target.value }))}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              addFeature();
            }
          }}
        />
        <select
          className="settings-input"
          value={draft.category}
          onChange={(event) => setDraft((prev) => ({ ...prev, category: event.target.value }))}
        >
          {FEATURE_CATEGORIES.map((category) => (
            <option key={category}>{category}</option>
          ))}
        </select>
        <select
          className="settings-input"
          value={draft.priority}
          onChange={(event) => setDraft((prev) => ({ ...prev, priority: event.target.value }))}
        >
          {FEATURE_PRIORITIES.map((priority) => (
            <option key={priority}>{priority}</option>
          ))}
        </select>
        <Toggle
          checked={draft.visible}
          onChange={(value) => setDraft((prev) => ({ ...prev, visible: value }))}
        />
        <button type="button" className="settings-secondary-btn" onClick={addFeature}>
          Add
        </button>
      </div>
    </div>
  );
}

// Tabs visible to admin role (business/operational only — no General/system controls).
const ADMIN_ALLOWED_TAB_IDS = ["browse", "filters", "bookingRules", "contracts"];

export default function Settings() {
  const { user } = useAuth() || {};
  const role = String(user?.role || "").toLowerCase();
  const isTechnicalAdmin = role === "technical_admin";
  const isAdminRole = role === "admin";
  const canManageContractTemplates = isTechnicalAdmin;

  // technical_admin sees all tabs; admin sees only business/operational tabs.
  const visibleTabs = isTechnicalAdmin
    ? tabs
    : tabs.filter((t) => ADMIN_ALLOWED_TAB_IDS.includes(t.id));

  const [activeTab, setActiveTab] = useState(() =>
    isTechnicalAdmin ? "browse" : ADMIN_ALLOWED_TAB_IDS[0]
  );
  const [settings, setSettings] = useState(defaultSettings);
  const [savedSettings, setSavedSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [heroImageError, setHeroImageError] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(getImageSrc(defaultSettings.general.logo));
  const [logoImageError, setLogoImageError] = useState("");
  const [priceRangeErrors, setPriceRangeErrors] = useState({});
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentForm, setPaymentForm] = useState(initialPaymentForm);
  const [paymentEditingId, setPaymentEditingId] = useState("");
  const [paymentErrors, setPaymentErrors] = useState({});
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(true);
  const [qrFile, setQrFile] = useState(null);
  const [qrPreview, setQrPreview] = useState("");
  const [deactivatePaymentModal, setDeactivatePaymentModal] = useState(null);
  const [restrictedAreas, setRestrictedAreas] = useState([]);
  const [restrictedAreaForm, setRestrictedAreaForm] = useState(initialRestrictedAreaForm);
  const [restrictedAreaEditingId, setRestrictedAreaEditingId] = useState("");
  const [restrictedAreaErrors, setRestrictedAreaErrors] = useState({});
  const [restrictedAreaLoading, setRestrictedAreaLoading] = useState(true);
  const [restrictedAreaSaving, setRestrictedAreaSaving] = useState(false);
  const [penaltyRule, setPenaltyRule] = useState(null);
  const [penaltyRuleForm, setPenaltyRuleForm] = useState(defaultPenaltyRule);
  const [penaltyRuleErrors, setPenaltyRuleErrors] = useState({});
  const [penaltyRuleLoading, setPenaltyRuleLoading] = useState(true);
  const [penaltyRuleSaving, setPenaltyRuleSaving] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionLoadingTitle, setActionLoadingTitle] = useState("");
  const [expandedContractSection, setExpandedContractSection] = useState(0);

  // If admin navigates directly to a restricted tab, redirect to first allowed tab.
  useEffect(() => {
    if (!isTechnicalAdmin && !ADMIN_ALLOWED_TAB_IDS.includes(activeTab)) {
      setActiveTab(ADMIN_ALLOWED_TAB_IDS[0]);
    }
  }, [activeTab, isTechnicalAdmin]);

  useEffect(() => {
    let active = true;
    const loadSettings = async () => {
      try {
        setLoading(true);
        setPaymentLoading(true);
        setRestrictedAreaLoading(true);
        setPenaltyRuleLoading(true);
        const paymentMethodsRequest = isTechnicalAdmin
          ? api.get("/payment-methods")
          : Promise.resolve({ data: { paymentMethods: [] } });
        const [settingsRes, paymentRes, restrictedAreasRes, penaltyRuleRes] = await Promise.all([
          api.get("/settings"),
          paymentMethodsRequest,
          api.get("/admin/restricted-areas"),
          api.get("/admin/penalty-extension-rules"),
        ]);
        if (!active) return;
        const nextSettings = normalizeSettings(settingsRes.data.settings);
        setSettings(nextSettings);
        setSavedSettings(nextSettings);
        setImagePreview(getImageSrc(nextSettings.hero.image));
        setHeroImageError("");
        setLogoPreview(getImageSrc(nextSettings.general.logo));
        setLogoImageError("");
        setPaymentMethods(paymentRes.data.paymentMethods || []);
        setRestrictedAreas(restrictedAreasRes.data.restrictedAreas || []);
        setPenaltyRule(penaltyRuleRes.data.rule || null);
        setPenaltyRuleForm(ruleToPenaltyForm(penaltyRuleRes.data.rule));
      } catch (err) {
        console.error("Load settings error:", err);
      } finally {
        if (active) {
          setLoading(false);
          setPaymentLoading(false);
          setRestrictedAreaLoading(false);
          setPenaltyRuleLoading(false);
        }
      }
    };
    loadSettings();
    return () => {
      active = false;
    };
  }, [isTechnicalAdmin]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(""), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const hasUnsavedChanges = useMemo(
    () => settingsSignature(settings, Boolean(imageFile || logoFile)) !== settingsSignature(savedSettings, false),
    [imageFile, logoFile, savedSettings, settings]
  );

  const updateHero = (key, value) => {
    setSettings((prev) => ({ ...prev, hero: { ...prev.hero, [key]: value } }));
  };

  const updateFilters = (key, value) => {
    setSettings((prev) => ({ ...prev, filters: { ...prev.filters, [key]: value } }));
  };

  const updateFeatureCatalog = (featureCatalog) => {
    const normalized = normalizeFeatureCatalog(featureCatalog);
    setSettings((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        featureCatalog: normalized,
        features: normalized.map((feature) => feature.label),
      },
    }));
  };

  const updateFilterBehavior = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        behavior: { ...prev.filters.behavior, [key]: value },
      },
    }));
  };

  const updateGeneral = (key, value) => {
    setSettings((prev) => ({ ...prev, general: { ...prev.general, [key]: value } }));
  };

  const updatePreferences = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      general: {
        ...prev.general,
        preferences: { ...prev.general.preferences, [key]: value },
      },
    }));
  };

  const updateControls = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      general: {
        ...prev.general,
        controls: { ...prev.general.controls, [key]: value },
      },
    }));
  };

  const updateExtraCharges = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      bookingRules: {
        ...prev.bookingRules,
        extraCharges: {
          ...prev.bookingRules.extraCharges,
          [key]: value,
        },
      },
    }));
  };

  const updateTripPreferences = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      bookingRules: {
        ...prev.bookingRules,
        tripPreferences: {
          ...prev.bookingRules.tripPreferences,
          [key]: value,
        },
      },
    }));
  };

  const updateVehicleLuggageBagLimit = (category, sizeKey, value) => {
    setSettings((prev) => {
      const current = prev.bookingRules.tripPreferences.vehicleLuggageBagLimits || {};
      return {
        ...prev,
        bookingRules: {
          ...prev.bookingRules,
          tripPreferences: {
            ...prev.bookingRules.tripPreferences,
            vehicleLuggageBagLimits: {
              ...current,
              [category]: {
                ...current[category],
                [sizeKey]: value === "" ? "" : Number(value),
              },
            },
          },
        },
      };
    });
  };

  const updateVehicleLuggageLimit = (category, field, value) => {
    setSettings((prev) => {
      const current = prev.bookingRules.tripPreferences.vehicleLuggageLimits || {};
      return {
        ...prev,
        bookingRules: {
          ...prev.bookingRules,
          tripPreferences: {
            ...prev.bookingRules.tripPreferences,
            vehicleLuggageLimits: {
              ...current,
              [category]: {
                ...current[category],
                [field]: value === "" ? "" : Number(value),
              },
            },
          },
        },
      };
    });
  };

  const updateContractTemplate = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      contractTemplate: {
        ...prev.contractTemplate,
        [key]: value,
      },
    }));
  };

  const updateContractSection = (index, patch) => {
    setSettings((prev) => {
      const sections = [...(prev.contractTemplate?.sections || [])];
      sections[index] = { ...sections[index], ...patch };
      return {
        ...prev,
        contractTemplate: {
          ...prev.contractTemplate,
          sections: sections.map((section, sectionIndex) => ({
            ...section,
            sortOrder: sectionIndex + 1,
          })),
        },
      };
    });
  };

  const addContractSection = () => {
    setSettings((prev) => ({
      ...prev,
      contractTemplate: {
        ...prev.contractTemplate,
        sections: [
          ...(prev.contractTemplate?.sections || []),
          {
            title: "New Contract Section",
            body: "",
            sortOrder: (prev.contractTemplate?.sections || []).length + 1,
            enabled: true,
          },
        ],
      },
    }));
  };

  const removeContractSection = (index) => {
    setSettings((prev) => ({
      ...prev,
      contractTemplate: {
        ...prev.contractTemplate,
        sections: (prev.contractTemplate?.sections || [])
          .filter((_, sectionIndex) => sectionIndex !== index)
          .map((section, sectionIndex) => ({ ...section, sortOrder: sectionIndex + 1 })),
      },
    }));
  };

  const resetContractTemplate = () => {
    setConfirmAction({
      title: "Reset contract template?",
      message: "Unsaved contract edits will be replaced with the FleetX default template.",
      confirmLabel: "Reset Template",
      tone: "warning",
      onConfirm: () => {
        setSettings((prev) => ({
          ...prev,
          contractTemplate: normalizeContractTemplate(defaultSettings.contractTemplate),
        }));
        setConfirmAction(null);
        setToast("Contract template reset. Save settings to apply it.");
      },
    });
  };

  const updateInfoCard = (index, key, value) => {
    setSettings((prev) => {
      const cards = [...prev.hero.infoCards];
      cards[index] = { ...cards[index], [key]: value };
      return { ...prev, hero: { ...prev.hero, infoCards: cards } };
    });
  };

  const addInfoCard = () => {
    setSettings((prev) => ({
      ...prev,
      hero: {
        ...prev.hero,
        infoCards: [...prev.hero.infoCards, { title: "", description: "" }],
      },
    }));
  };

  const removeInfoCard = (index) => {
    setSettings((prev) => ({
      ...prev,
      hero: {
        ...prev.hero,
        infoCards: prev.hero.infoCards.filter((_, itemIndex) => itemIndex !== index),
      },
    }));
  };

  const updatePriceRange = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        priceRange: { ...prev.filters.priceRange, [key]: value },
      },
    }));
    setPriceRangeErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const updateSocialLink = (index, key, value) => {
    setSettings((prev) => {
      const socialLinks = [...prev.general.socialLinks];
      socialLinks[index] = { ...socialLinks[index], [key]: value };
      return { ...prev, general: { ...prev.general, socialLinks } };
    });
  };

  const addSocialLink = () => {
    setSettings((prev) => ({
      ...prev,
      general: {
        ...prev.general,
        socialLinks: [...prev.general.socialLinks, { label: "New Link", url: "" }],
      },
    }));
  };

  const removeSocialLink = (index) => {
    setSettings((prev) => ({
      ...prev,
      general: {
        ...prev.general,
        socialLinks: prev.general.socialLinks.filter((_, itemIndex) => itemIndex !== index),
      },
    }));
  };

  const handleImage = (file) => {
    if (!file) return;

    const error = validateHeroFile(file);
    if (error) {
      setHeroImageError(error);
      setToast(error);
      return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setHeroImageError("");
  };

  const handleLogo = (file) => {
    if (!file) return;
    const error = validateLogoFile(file);
    if (error) {
      setLogoImageError(error);
      setToast(error);
      return;
    }

    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setLogoImageError("");
  };

  const loadPaymentMethods = async () => {
    if (!isTechnicalAdmin) {
      setPaymentMethods([]);
      setPaymentLoading(false);
      return;
    }

    try {
      setPaymentLoading(true);
      const res = await api.get("/payment-methods");
      setPaymentMethods(res.data.paymentMethods || []);
    } catch (err) {
      setToast(err?.response?.data?.message || "Unable to load payment methods.");
    } finally {
      setPaymentLoading(false);
    }
  };

  const resetPaymentForm = () => {
    setPaymentForm(initialPaymentForm);
    setPaymentEditingId("");
    setPaymentErrors({});
    setQrFile(null);
    setQrPreview("");
  };

  const updatePaymentForm = (key, value) => {
    setPaymentForm((prev) => ({ ...prev, [key]: value }));
    setPaymentErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const editPaymentMethod = (method) => {
    setPaymentEditingId(method._id);
    setPaymentForm({
      name: method.name || "",
      category: normalizePaymentCategoryLabel(method.category),
      accountName: method.accountName || DEFAULT_PAYMENT_ACCOUNT_NAME,
      accountNumber: method.accountNumber || "",
      instructions: method.instructions || "",
      isActive: method.isActive !== false,
    });
    setPaymentErrors({});
    setQrFile(null);
    setQrPreview(getImageSrc(method.qrCodeUrl || ""));
  };

  const handleQrCode = (file) => {
    if (!file) return;

    const error = validateQrFile(file);
    if (error) {
      setPaymentErrors((prev) => ({ ...prev, qrCode: error }));
      return;
    }

    setQrFile(file);
    setQrPreview(URL.createObjectURL(file));
    setPaymentErrors((prev) => {
      const next = { ...prev };
      delete next.qrCode;
      return next;
    });
  };

  const validatePaymentForm = () => {
    const errors = {};

    if (!paymentForm.name.trim()) errors.name = "Method name is required.";
    if (!paymentForm.category.trim()) errors.category = "Category is required.";

    setPaymentErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const savePaymentMethod = async (event) => {
    event.preventDefault();

    if (!validatePaymentForm()) return;

    try {
      setPaymentSaving(true);
      const formData = new FormData();
      formData.append("name", paymentForm.name.trim());
      formData.append("category", paymentForm.category.trim());
      formData.append(
        "accountName",
        paymentForm.accountName.trim() || DEFAULT_PAYMENT_ACCOUNT_NAME
      );
      formData.append("accountNumber", paymentForm.accountNumber.trim());
      formData.append("instructions", paymentForm.instructions.trim());
      formData.append("isActive", String(paymentForm.isActive));
      if (qrFile) formData.append("qrCode", qrFile);

      if (paymentEditingId) {
        await api.patch(`/payment-methods/${paymentEditingId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setToast("Payment method updated.");
      } else {
        await api.post("/payment-methods", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setToast("Payment method added.");
      }

      resetPaymentForm();
      await loadPaymentMethods();
    } catch (err) {
      if (err?.response?.data?.errors) {
        setPaymentErrors(err.response.data.errors);
      }
      setToast(err?.response?.data?.message || "Unable to save payment method.");
    } finally {
      setPaymentSaving(false);
    }
  };

  const openDeactivatePaymentMethod = (method) => {
    setDeactivatePaymentModal({ method, reason: "", error: "", submitting: false });
  };

  const confirmDeactivatePaymentMethod = async () => {
    if (!deactivatePaymentModal?.method?._id || deactivatePaymentModal.submitting) return;
    const reason = String(deactivatePaymentModal.reason || "").trim();
    if (!reason) {
      setDeactivatePaymentModal((prev) => ({ ...prev, error: "Please enter a reason for deactivating." }));
      return;
    }
    setDeactivatePaymentModal((prev) => ({ ...prev, error: "", submitting: true }));
    try {
      await api.patch(`/payment-methods/${deactivatePaymentModal.method._id}/deactivate`, { reason });
      if (paymentEditingId === deactivatePaymentModal.method._id) {
        resetPaymentForm();
      }
      setDeactivatePaymentModal(null);
      await loadPaymentMethods();
      setToast("Payment method deactivated.");
    } catch (err) {
      setDeactivatePaymentModal((prev) => ({
        ...prev,
        submitting: false,
        error: err?.response?.data?.message || "Unable to deactivate payment method.",
      }));
    }
  };

  const reactivatePaymentMethod = async (method) => {
    try {
      setPaymentSaving(true);
      await api.patch(`/payment-methods/${method._id}/activate`);
      await loadPaymentMethods();
      setToast("Payment method reactivated.");
    } catch (err) {
      setToast(err?.response?.data?.message || "Unable to reactivate payment method.");
    } finally {
      setPaymentSaving(false);
    }
  };

  const loadRestrictedAreas = async () => {
    try {
      setRestrictedAreaLoading(true);
      const res = await api.get("/admin/restricted-areas");
      setRestrictedAreas(res.data.restrictedAreas || []);
    } catch (err) {
      setToast(err?.response?.data?.message || "Unable to load restricted areas.");
    } finally {
      setRestrictedAreaLoading(false);
    }
  };

  const resetRestrictedAreaForm = () => {
    setRestrictedAreaForm(initialRestrictedAreaForm);
    setRestrictedAreaEditingId("");
    setRestrictedAreaErrors({});
  };

  const updateRestrictedAreaForm = (key, value) => {
    setRestrictedAreaForm((prev) => ({ ...prev, [key]: value }));
    setRestrictedAreaErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const toggleRestrictedTarget = (targets) => {
    setRestrictedAreaForm((prev) => {
      const current = new Set(prev.appliesTo || []);
      const allSelected = targets.every((target) => current.has(target));
      targets.forEach((target) => {
        if (allSelected) current.delete(target);
        else current.add(target);
      });
      return { ...prev, appliesTo: Array.from(current) };
    });
    setRestrictedAreaErrors((prev) => {
      const next = { ...prev };
      delete next.appliesTo;
      return next;
    });
  };

  const editRestrictedArea = (rule) => {
    setRestrictedAreaEditingId(rule._id);
    setRestrictedAreaForm({
      areaName: rule.areaName || "",
      keywords: (rule.keywords || []).join(", "),
      appliesTo: rule.appliesTo?.length ? rule.appliesTo : ["destination", "dropoff"],
      status: rule.status || "active",
      message: rule.message || DEFAULT_RESTRICTED_AREA_MESSAGE,
    });
    setRestrictedAreaErrors({});
    setActiveTab("bookingRules");
  };

  const validateRestrictedAreaForm = () => {
    const errors = {};
    if (!restrictedAreaForm.areaName.trim()) errors.areaName = "Area name is required.";
    if (!splitKeywords(restrictedAreaForm.keywords).length) {
      errors.keywords = "Add at least one keyword.";
    }
    if (!restrictedAreaForm.appliesTo?.length) {
      errors.appliesTo = "Select at least one booking field.";
    }

    setRestrictedAreaErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveRestrictedArea = async (event) => {
    event.preventDefault();
    if (!validateRestrictedAreaForm()) return;

    const payload = {
      areaName: restrictedAreaForm.areaName.trim(),
      keywords: splitKeywords(restrictedAreaForm.keywords),
      appliesTo: restrictedAreaForm.appliesTo,
      status: restrictedAreaForm.status,
      message: restrictedAreaForm.message.trim() || DEFAULT_RESTRICTED_AREA_MESSAGE,
    };

    try {
      setRestrictedAreaSaving(true);
      if (restrictedAreaEditingId) {
        await api.patch(`/admin/restricted-areas/${restrictedAreaEditingId}`, payload);
        setToast("Restricted area updated.");
      } else {
        await api.post("/admin/restricted-areas", payload);
        setToast("Restricted area added.");
      }
      resetRestrictedAreaForm();
      await loadRestrictedAreas();
    } catch (err) {
      if (err?.response?.data?.errors) {
        setRestrictedAreaErrors(err.response.data.errors);
      }
      setToast(err?.response?.data?.message || "Unable to save restricted area.");
    } finally {
      setRestrictedAreaSaving(false);
    }
  };

  const setRestrictedAreaStatus = async (rule, status) => {
    try {
      setRestrictedAreaSaving(true);
      await api.patch(`/admin/restricted-areas/${rule._id}/status`, { status });
      await loadRestrictedAreas();
      setToast(status === "active" ? "Restricted area activated." : "Restricted area deactivated.");
    } catch (err) {
      setToast(err?.response?.data?.message || "Unable to update restricted area.");
    } finally {
      setRestrictedAreaSaving(false);
    }
  };

  const deleteRestrictedArea = async (rule) => {
    setConfirmAction({
      title: "Delete restricted area?",
      message: `Delete "${rule.areaName}"? This rule will stop blocking matching booking locations.`,
      confirmLabel: "Delete Rule",
      loadingLabel: "Deleting...",
      tone: "danger",
      onConfirm: async () => {
        try {
          setConfirmAction(null);
          setActionLoadingTitle("Deleting restricted area...");
          setRestrictedAreaSaving(true);
          await api.delete(`/admin/restricted-areas/${rule._id}`);
          if (restrictedAreaEditingId === rule._id) resetRestrictedAreaForm();
          await loadRestrictedAreas();
          setToast("Restricted area deleted.");
          setConfirmAction(null);
        } catch (err) {
          setToast(err?.response?.data?.message || "Unable to delete restricted area.");
        } finally {
          setRestrictedAreaSaving(false);
          setActionLoadingTitle("");
        }
      },
    });
  };


  const updatePenaltyRuleForm = (key, value) => {
    setPenaltyRuleForm((prev) => ({ ...prev, [key]: value }));
    setPenaltyRuleErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validatePenaltyRuleForm = () => {
    const errors = {};
    const gracePeriodHours = Number(penaltyRuleForm.gracePeriodHours);
    const penaltyValue = Number(penaltyRuleForm.penaltyValue);

    if (!Number.isFinite(gracePeriodHours) || gracePeriodHours < 0) {
      errors.gracePeriodHours = "Grace period cannot be negative.";
    }
    if (!PENALTY_TYPE_OPTIONS.some((option) => option.value === penaltyRuleForm.penaltyType)) {
      errors.penaltyType = "Select a valid penalty type.";
    }
    if (!Number.isFinite(penaltyValue) || penaltyValue < 0) {
      errors.penaltyValue = "Penalty value cannot be negative.";
    } else if (penaltyRuleForm.penaltyType === "percentage_of_daily_rate" && penaltyValue > 500) {
      errors.penaltyValue = "Percentage must be between 0 and 500.";
    } else if (penaltyRuleForm.penaltyType === "multiplier_of_daily_rate" && penaltyValue > 10) {
      errors.penaltyValue = "Multiplier must be between 0 and 10.";
    }

    setPenaltyRuleErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const savePenaltyRule = async (event) => {
    event.preventDefault();
    if (!validatePenaltyRuleForm()) return;

    const payload = {
      ...penaltyRuleForm,
      gracePeriodHours: Number(penaltyRuleForm.gracePeriodHours),
      penaltyValue: Number(penaltyRuleForm.penaltyValue),
    };

    try {
      setPenaltyRuleSaving(true);
      const res = await api.put("/admin/penalty-extension-rules", payload);
      setPenaltyRule(res.data.rule || null);
      setPenaltyRuleForm(ruleToPenaltyForm(res.data.rule));
      setToast("Penalty and extension rules saved.");
    } catch (err) {
      if (err?.response?.data?.errors) {
        setPenaltyRuleErrors(err.response.data.errors);
      }
      setToast(err?.response?.data?.message || "Unable to save penalty rules.");
    } finally {
      setPenaltyRuleSaving(false);
    }
  };

  const setPenaltyRuleStatus = async (status) => {
    try {
      setPenaltyRuleSaving(true);
      const res = await api.patch("/admin/penalty-extension-rules/status", { status });
      setPenaltyRule(res.data.rule || null);
      setPenaltyRuleForm(ruleToPenaltyForm(res.data.rule));
      setToast(status === "active" ? "Penalty rules activated." : "Penalty rules deactivated.");
    } catch (err) {
      setToast(err?.response?.data?.message || "Unable to update penalty rule status.");
    } finally {
      setPenaltyRuleSaving(false);
    }
  };

  const saveSettings = async () => {
    const { minimumPrice, maximumPrice, priceStep } = settings.filters.priceRange;
    const priceErrors = {};
    if (Number(minimumPrice) < 0) {
      priceErrors.minimumPrice = "Minimum price must be 0 or greater.";
    }
    if (Number(maximumPrice) <= Number(minimumPrice)) {
      priceErrors.maximumPrice = "Maximum price must be greater than minimum price.";
    }
    if (Number(priceStep) <= 0) {
      priceErrors.priceStep = "Step must be greater than 0.";
    }
    if (Object.keys(priceErrors).length > 0) {
      setPriceRangeErrors(priceErrors);
      setActiveTab("filters");
      setToast("Please fix the price range settings before saving.");
      return;
    }
    const tp = settings.bookingRules?.tripPreferences || {};
    const maxWeight = Number(tp.maxPlanTripLuggageWeightKg);
    const threshold = Number(tp.heavyLuggageThresholdKg);
    if (Number(tp.maxPlanTripPassengers) <= 0) {
      setActiveTab("bookingRules");
      setToast("Max passengers must be greater than 0.");
      return;
    }
    if (Number(tp.maxPlanTripLuggageBags) < 0) {
      setActiveTab("bookingRules");
      setToast("Max luggage bags must be 0 or greater.");
      return;
    }
    if (maxWeight <= 0) {
      setActiveTab("bookingRules");
      setToast("Max luggage weight must be greater than 0.");
      return;
    }
    if (threshold <= 0) {
      setActiveTab("bookingRules");
      setToast("Heavy luggage threshold must be greater than 0.");
      return;
    }
    if (threshold > maxWeight) {
      setActiveTab("bookingRules");
      setToast("Heavy luggage threshold cannot exceed max luggage weight.");
      return;
    }
    if (!String(tp.heavyLuggageWarningText || "").trim()) {
      setActiveTab("bookingRules");
      setToast("Heavy luggage warning text cannot be empty.");
      return;
    }
    if (!String(tp.vehicleLuggageOverLimitText || "").trim()) {
      setActiveTab("bookingRules");
      setToast("Over-limit error text cannot be empty.");
      return;
    }
    const vehicleLimits = tp.vehicleLuggageLimits || {};
    for (const cat of ["sedan", "hatchback", "mpv", "suv", "van", "pickup", "default"]) {
      const entry = vehicleLimits[cat] || {};
      const soft = Number(entry.soft);
      const hard = Number(entry.hard);
      if (!soft || soft <= 0) {
        setActiveTab("bookingRules");
        setToast(`${cat.charAt(0).toUpperCase() + cat.slice(1)} warning threshold must be greater than 0.`);
        return;
      }
      if (!hard || hard <= 0) {
        setActiveTab("bookingRules");
        setToast(`${cat.charAt(0).toUpperCase() + cat.slice(1)} hard limit must be greater than 0.`);
        return;
      }
      if (hard < soft) {
        setActiveTab("bookingRules");
        setToast(`${cat.charAt(0).toUpperCase() + cat.slice(1)}: hard limit must be ≥ warning threshold.`);
        return;
      }
    }
    const bagLimits = tp.vehicleLuggageBagLimits || {};
    for (const cat of ["sedan", "hatchback", "mpv", "suv", "van", "pickup", "default"]) {
      const entry = bagLimits[cat] || {};
      for (const sizeKey of ["small", "medium", "large", "extraLarge"]) {
        const n = Number(entry[sizeKey]);
        if (!Number.isFinite(n) || n < 0) {
          setActiveTab("bookingRules");
          const label = cat.charAt(0).toUpperCase() + cat.slice(1);
          const sizeLabel = sizeKey === "extraLarge" ? "Extra Large" : sizeKey.charAt(0).toUpperCase() + sizeKey.slice(1);
          setToast(`${label} — ${sizeLabel} bag limit must be 0 or greater.`);
          return;
        }
      }
    }

    const extraChargeRules = settings.bookingRules?.extraCharges || {};
    if (
      extraChargeRules.deliveryFeeMode === "per_km" &&
      Number(extraChargeRules.perKmDeliveryFee || 0) <= 0
    ) {
      setActiveTab("bookingRules");
      setToast("Succeeding rate per kilometer must be greater than 0 for per-km delivery fees.");
      return;
    }
    setPriceRangeErrors({});
    const heroError = validateHeroFile(imageFile);
    if (heroError) {
      setHeroImageError(heroError);
      setActiveTab("browse");
      setToast(heroError);
      return;
    }
    const logoError = validateLogoFile(logoFile);
    if (logoError) {
      setLogoImageError(logoError);
      setActiveTab("general");
      setToast(logoError);
      return;
    }

    const contractTemplate = normalizeContractTemplate(settings.contractTemplate);
    if (!contractTemplate.title) {
      setActiveTab("contracts");
      setToast("Contract title is required.");
      return;
    }
    if (!contractTemplate.sections.some((section) => section.enabled !== false && section.title)) {
      setActiveTab("contracts");
      setToast("At least one enabled contract section is required.");
      return;
    }

    try {
      setSaving(true);
      const formData = new FormData();
      formData.append("hero", JSON.stringify(settings.hero));
      formData.append("filters", JSON.stringify(settings.filters));
      formData.append("general", JSON.stringify(settings.general));
      formData.append("bookingRules", JSON.stringify(settings.bookingRules));
      formData.append("contractTemplate", JSON.stringify(contractTemplate));
      if (imageFile) formData.append("heroImage", imageFile);
      if (logoFile) formData.append("logoImage", logoFile);

      const res = await api.put("/settings", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const nextSettings = normalizeSettings(res.data.settings);
      setSettings(nextSettings);
      setSavedSettings(nextSettings);
      setImageFile(null);
      setLogoFile(null);
      setImagePreview(getImageSrc(nextSettings.hero.image));
      setHeroImageError("");
      setLogoPreview(getImageSrc(nextSettings.general.logo));
      setLogoImageError("");
      window.dispatchEvent(
        new CustomEvent("fleetx-settings-updated", { detail: { settings: nextSettings } })
      );
      setToast("Settings saved successfully.");
    } catch (err) {
      console.error("Save settings error:", err);
      if (err?.response?.data?.errors?.heroImage) {
        setHeroImageError(err.response.data.errors.heroImage);
      }
      if (err?.response?.data?.errors?.logoImage) {
        setLogoImageError(err.response.data.errors.logoImage);
      }
      setToast(err?.response?.data?.message || "Unable to save settings.");
    } finally {
      setSaving(false);
    }
  };

  const resetChanges = () => {
    setSettings(savedSettings);
    setImageFile(null);
    setLogoFile(null);
    setImagePreview(getImageSrc(savedSettings.hero.image));
    setHeroImageError("");
    setLogoPreview(getImageSrc(savedSettings.general.logo));
    setLogoImageError("");
  };

  const pageActions = (
    <button
      type="button"
      className="settings-primary-btn"
      onClick={saveSettings}
      disabled={saving || loading}
    >
      {saving ? "Saving..." : "Save Settings"}
    </button>
  );
  const settingsLoadingTitle = actionLoadingTitle
    ? actionLoadingTitle
    : saving
    ? activeTab === "contracts"
      ? "Saving contract template..."
      : activeTab === "bookingRules"
      ? "Saving booking rules..."
      : "Saving settings..."
    : paymentSaving
    ? "Updating payment method..."
    : restrictedAreaSaving
    ? "Saving booking rules..."
    : penaltyRuleSaving
    ? "Saving booking rules..."
    : deactivatePaymentModal?.submitting
    ? "Deactivating payment method..."
    : "";

  return (
    <AdminShell
      pageClassName="admin-shell--settings"
      title="Settings"
      subtitle="Manage system content, customer page, and filters."
      eyebrow="SETTINGS"
      actions={pageActions}
    >
      <div className="settings-page">
        <AdminLoadingOverlay
          open={Boolean(settingsLoadingTitle)}
          title={settingsLoadingTitle}
          message="Please wait while FleetX saves the latest settings."
        />
        {isAdminRole ? (
          <p className="settings-role-note">
            You can manage business settings. System controls are reserved for Technical Admin.
          </p>
        ) : null}
        <div className="settings-tabs" role="tablist" aria-label="Settings sections">
          {visibleTabs.map((tab) => (
            <button
              type="button"
              key={tab.id}
              className={`settings-tab ${activeTab === tab.id ? "is-active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? <SettingsSkeleton /> : null}

        {!loading && activeTab === "browse" ? (
          <div className="settings-browse-grid">
            <div className="settings-stack">
              <section className="settings-card settings-hero-card">
                <div className="settings-card-head">
                  <h2>Hero Section</h2>
                </div>
                <div className="settings-hero-editor">
                  <div className="settings-stack">
                    <Field label="Hero Title" helper="Headline displayed on the customer browse page hero.">
                      <input
                        className="settings-input"
                        value={settings.hero.title}
                        onChange={(event) => updateHero("title", event.target.value)}
                      />
                    </Field>
                    <Field label="Hero Subtitle" helper="Subtitle text that appears below the headline.">
                      <textarea
                        className="settings-input settings-textarea"
                        value={settings.hero.subtitle}
                        onChange={(event) => updateHero("subtitle", event.target.value)}
                      />
                    </Field>
                    <Field label="CTA Text" helper="Text for the primary call-to-action button.">
                      <input
                        className="settings-input"
                        value={settings.hero.ctaText}
                        onChange={(event) => updateHero("ctaText", event.target.value)}
                      />
                    </Field>
                  </div>
                  <div className="settings-image-column">
                    <span className="settings-mini-label">Hero Image</span>
                    <label
                      className="settings-upload"
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={(event) => {
                        event.preventDefault();
                        handleImage(event.dataTransfer.files?.[0]);
                      }}
                    >
                      {imagePreview ? <img src={imagePreview} alt="Hero preview" /> : <strong>Drop hero image here</strong>}
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp"
                        onChange={(event) => handleImage(event.target.files?.[0])}
                      />
                    </label>
                    <div className="settings-upload-actions">
                      <label className="settings-secondary-btn">
                        Change Image
                        <input
                          hidden
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp"
                          onChange={(event) => handleImage(event.target.files?.[0])}
                        />
                      </label>
                      <span>Recommended: 1600 x 900px (JPG/PNG/WEBP)</span>
                    </div>
                    {heroImageError ? (
                      <small className="settings-inline-error">{heroImageError}</small>
                    ) : null}
                    <small>High quality image shown on the hero section.</small>
                  </div>
                </div>
              </section>

              <section className="settings-card settings-feature-chips-card">
                <div className="settings-card-head">
                  <h2>Feature Chips</h2>
                  <p>These highlights will appear as feature chips on the customer browse page.</p>
                </div>
                <FeatureChipsEditor
                  values={settings.hero.chips}
                  onChange={(chips) => updateHero("chips", chips)}
                />
              </section>

              <section className="settings-card settings-info-cards-card">
                <div className="settings-card-head">
                  <h2>Info Cards Content</h2>
                  <p>These info cards appear beside the hero section on the customer browse page.</p>
                </div>
                <div className="settings-info-list">
                  {settings.hero.infoCards.map((card, index) => (
                    <div className="settings-info-item" key={`info-${index}`}>
                      <span className="settings-drag-handle">::</span>
                      <input
                        className="settings-input"
                        value={card.title}
                        placeholder="Title"
                        onChange={(event) => updateInfoCard(index, "title", event.target.value)}
                      />
                      <input
                        className="settings-input"
                        value={card.description}
                        placeholder="Description"
                        onChange={(event) => updateInfoCard(index, "description", event.target.value)}
                      />
                      <button type="button" className="settings-icon-btn" onClick={() => removeInfoCard(index)}>
                        x
                      </button>
                    </div>
                  ))}
                </div>
                <button type="button" className="settings-secondary-btn settings-add-info" onClick={addInfoCard}>
                  + Add Info Card
                </button>
              </section>
            </div>

            <div className="settings-stack settings-preview-column">
              <section className="settings-card settings-live-card">
                <div className="settings-card-head">
                  <h2>Live Preview</h2>
                  <p>This is how your customer browse page hero section will appear.</p>
                </div>
                <div className="settings-hero-preview">
                  <div className="settings-hero-preview__top">
                    <div>
                      <h3>{settings.hero.title}</h3>
                      <p>{settings.hero.subtitle}</p>
                      <button type="button">{settings.hero.ctaText}</button>
                    </div>
                    {imagePreview ? <img src={imagePreview} alt="Hero live preview" /> : null}
                  </div>
                  <div className="settings-preview-chips">
                    {settings.hero.chips.map((chip, index) => (
                      <span key={`${chip}-${index}`}>{chip}</span>
                    ))}
                  </div>
                  <div className="settings-preview-info">
                    {settings.hero.infoCards.slice(0, 3).map((card, index) => (
                      <article key={`${card.title}-${index}`}>
                        <span>{card.title || "Info card"}</span>
                        <strong>{card.description || "Description"}</strong>
                      </article>
                    ))}
                  </div>
                </div>
              </section>

              <section className="settings-card settings-publishing-card">
                <div className="settings-card-head">
                  <h2>Publishing / Status</h2>
                </div>
                <div className="settings-status-list">
                  <div><span>Last updated</span><strong>Today by Administrator</strong></div>
                  <div><span>Preview mode</span><em className="settings-pill is-live">Live Preview</em></div>
                  <div><span>Unsaved changes</span><em className={`settings-pill ${hasUnsavedChanges ? "is-dirty" : ""}`}>{hasUnsavedChanges ? "Yes" : "No"}</em></div>
                </div>
              </section>
            </div>
          </div>
        ) : null}

        {!loading && activeTab === "filters" ? (
          <div className="settings-filters-grid">
            <section className="settings-card">
              <div className="settings-card-head">
                <h2>Filter Categories</h2>
              </div>
              <ChipEditor title="Vehicle Categories" values={settings.filters.categories} onChange={(values) => updateFilters("categories", values)} placeholder="Add new category" />
              <ChipEditor title="Transmission" values={settings.filters.transmissions} onChange={(values) => updateFilters("transmissions", values)} placeholder="Add new transmission type" />
              <ChipEditor title="Fuel Types" values={settings.filters.fuels} onChange={(values) => updateFilters("fuels", values)} placeholder="Add new fuel type" />
              <ChipEditor title="Seat Range" values={settings.filters.seatRanges} onChange={(values) => updateFilters("seatRanges", values)} placeholder="Add new seat range" />
            </section>

            <section className="settings-card settings-feature-card">
              <div className="settings-card-head">
                <h2>Feature Manager</h2>
                <p>Add, edit, categorize, prioritize, and hide customer-facing vehicle features.</p>
              </div>
              <FeatureManager
                values={settings.filters.featureCatalog}
                onChange={updateFeatureCatalog}
              />
            </section>

            <div className="settings-stack">
              <section className="settings-card">
                <div className="settings-card-head">
                  <h2>Filter Behavior</h2>
                </div>
                <div className="settings-behavior-list">
                  <div><span><strong>Show Price Range</strong><small>Display price range filter on customer page</small></span><Toggle checked={settings.filters.behavior.showPriceRange} onChange={(value) => updateFilterBehavior("showPriceRange", value)} /></div>
                  <div><span><strong>Show Fuel Type</strong><small>Allow customers to filter by fuel type</small></span><Toggle checked={settings.filters.behavior.showFuelType} onChange={(value) => updateFilterBehavior("showFuelType", value)} /></div>
                  <div><span><strong>Show Features</strong><small>Display features filter on customer page</small></span><Toggle checked={settings.filters.behavior.showFeatures} onChange={(value) => updateFilterBehavior("showFeatures", value)} /></div>
                  <div><span><strong>Default Sort</strong><small>Default sorting applied on customer page</small></span><select className="settings-input" value={settings.filters.behavior.defaultSort} onChange={(event) => updateFilterBehavior("defaultSort", event.target.value)}><option>Recommended</option><option>Price Low to High</option><option>Price High to Low</option><option>Newest</option></select></div>
                  <div><span><strong>Default Open Sections</strong><small>Sections expanded by default</small></span><select className="settings-input" value={settings.filters.behavior.defaultOpenSections} onChange={(event) => updateFilterBehavior("defaultOpenSections", event.target.value)}><option>Vehicle Categories, Price</option><option>All Sections</option><option>Vehicle Categories Only</option></select></div>
                  <div><span><strong>Max Selected Filters</strong><small>Maximum number of filters customers can select</small></span><select className="settings-input" value={settings.filters.behavior.maxSelectedFilters} onChange={(event) => updateFilterBehavior("maxSelectedFilters", event.target.value)}><option>3</option><option>5</option><option>8</option><option>10</option></select></div>
                </div>
              </section>

              <section className="settings-card">
                <div className="settings-card-head">
                  <h2>Price Range Filter</h2>
                  <p>Set the minimum and maximum daily rental price used in the customer browse filter.</p>
                </div>
                <div className="settings-stack">
                  <Field label="Minimum Price">
                    <input
                      className="settings-input"
                      type="number"
                      min="0"
                      placeholder="0"
                      value={settings.filters.priceRange.minimumPrice}
                      onChange={(event) => updatePriceRange("minimumPrice", event.target.value)}
                    />
                    {priceRangeErrors.minimumPrice ? (
                      <small className="settings-inline-error">{priceRangeErrors.minimumPrice}</small>
                    ) : null}
                  </Field>
                  <Field label="Maximum Price">
                    <input
                      className="settings-input"
                      type="number"
                      min="0"
                      placeholder="15000"
                      value={settings.filters.priceRange.maximumPrice}
                      onChange={(event) => updatePriceRange("maximumPrice", event.target.value)}
                    />
                    {priceRangeErrors.maximumPrice ? (
                      <small className="settings-inline-error">{priceRangeErrors.maximumPrice}</small>
                    ) : null}
                  </Field>
                  <Field
                    label="Step / Interval"
                    helper="Controls how much the price slider increases per step."
                  >
                    <input
                      className="settings-input"
                      type="number"
                      min="1"
                      placeholder="500"
                      value={settings.filters.priceRange.priceStep}
                      onChange={(event) => updatePriceRange("priceStep", event.target.value)}
                    />
                    {priceRangeErrors.priceStep ? (
                      <small className="settings-inline-error">{priceRangeErrors.priceStep}</small>
                    ) : null}
                  </Field>
                  <div className="settings-price-range-preview">
                    Customer filter range: PHP {Number(settings.filters.priceRange.minimumPrice || 0).toLocaleString()} – PHP {Number(settings.filters.priceRange.maximumPrice || 15000).toLocaleString()}
                  </div>
                </div>
              </section>
            </div>

            <section className="settings-card settings-filter-preview-card">
              <div className="settings-card-head">
                <h2>Customer Filter Preview</h2>
                <p>This is how filters will appear on the customer browse page.</p>
              </div>
              <div className="settings-filter-preview">
                <button type="button">Clear all</button>
                <div className="settings-preview-filter-section">
                  <strong>Vehicle Categories</strong>
                  {settings.filters.categories.slice(0, 4).map((item, index) => (
                    <label key={item}><input type="checkbox" readOnly /> {item}<span>{[24, 36, 18, 12][index] || 8}</span></label>
                  ))}
                </div>
                <div className="settings-price-line"><strong>Price Range</strong><span>PHP {Number(settings.filters.priceRange?.minimumPrice || 0).toLocaleString()} - PHP {Number(settings.filters.priceRange?.maximumPrice || 15000).toLocaleString()}+</span><i /></div>
                <div className="settings-preview-filter-section">
                  <strong>Transmission</strong>
                  {settings.filters.transmissions.slice(0, 2).map((item, index) => (
                    <label key={item}><input type="checkbox" readOnly /> {item}<span>{[48, 42][index] || 10}</span></label>
                  ))}
                </div>
                <div className="settings-preview-filter-section">
                  <strong>Fuel Type</strong>
                  {settings.filters.fuels.slice(0, 3).map((item, index) => (
                    <label key={item}><input type="checkbox" readOnly /> {item}<span>{[50, 28, 12][index] || 10}</span></label>
                  ))}
                </div>
                <div className="settings-collapsed-row"><strong>Features</strong><span>+</span></div>
              </div>
              <button type="button" className="settings-view-customer-btn">View on Customer Page</button>
            </section>
          </div>
        ) : null}

        {!loading && activeTab === "bookingRules" ? (
          <div className="settings-payment-grid settings-booking-rules-grid">
            <section className="settings-card settings-trip-preferences-card">
              <div className="settings-card-head">
                <h2>Trip Preferences</h2>
                <p>
                  Configure booking input limits. These values apply to both Plan My Trip and Browse Vehicle booking,
                  and are served to the customer app via the settings API.
                </p>
              </div>

              <div className="settings-payment-form">
                <Field
                  label="Max passengers"
                  helper="Maximum number of passengers a customer can enter (must be ≥ 1). Applies to Plan My Trip and Browse Vehicle booking."
                >
                  <input
                    className="settings-input"
                    type="number"
                    min="1"
                    step="1"
                    value={settings.bookingRules.tripPreferences.maxPlanTripPassengers}
                    onChange={(event) =>
                      updateTripPreferences("maxPlanTripPassengers", Number(event.target.value))
                    }
                  />
                </Field>

                <Field
                  label="Max luggage bags"
                  helper="Maximum number of luggage bags a customer can enter (0 means no limit). Applies to Plan My Trip and Browse Vehicle booking."
                >
                  <input
                    className="settings-input"
                    type="number"
                    min="0"
                    step="1"
                    value={settings.bookingRules.tripPreferences.maxPlanTripLuggageBags}
                    onChange={(event) =>
                      updateTripPreferences("maxPlanTripLuggageBags", Number(event.target.value))
                    }
                  />
                </Field>

                <Field
                  label="Max estimated luggage weight (kg)"
                  helper="Maximum luggage weight in kilograms a customer can enter (must be > 0)."
                >
                  <input
                    className="settings-input"
                    type="number"
                    min="1"
                    step="1"
                    value={settings.bookingRules.tripPreferences.maxPlanTripLuggageWeightKg}
                    onChange={(event) =>
                      updateTripPreferences(
                        "maxPlanTripLuggageWeightKg",
                        Number(event.target.value)
                      )
                    }
                  />
                </Field>

                <Field
                  label="Heavy luggage threshold (kg)"
                  helper="Luggage above this weight is flagged as heavy. Must not exceed max weight above."
                >
                  <input
                    className="settings-input"
                    type="number"
                    min="1"
                    step="1"
                    value={settings.bookingRules.tripPreferences.heavyLuggageThresholdKg}
                    onChange={(event) =>
                      updateTripPreferences(
                        "heavyLuggageThresholdKg",
                        Number(event.target.value)
                      )
                    }
                  />
                </Field>

                <Field
                  label="Heavy luggage warning text"
                  helper="Shown as an amber advisory when luggage weight exceeds the warning threshold. Max 300 characters."
                >
                  <input
                    className="settings-input"
                    type="text"
                    maxLength={300}
                    value={settings.bookingRules.tripPreferences.heavyLuggageWarningText ?? ""}
                    onChange={(e) => updateTripPreferences("heavyLuggageWarningText", e.target.value)}
                  />
                </Field>

                <Field
                  label="Over-limit error text"
                  helper="Shown as a red error when luggage weight exceeds a vehicle's hard limit. Max 300 characters."
                >
                  <input
                    className="settings-input"
                    type="text"
                    maxLength={300}
                    value={settings.bookingRules.tripPreferences.vehicleLuggageOverLimitText ?? ""}
                    onChange={(e) => updateTripPreferences("vehicleLuggageOverLimitText", e.target.value)}
                  />
                </Field>

                <div className="settings-payment-active-row">
                  <span>
                    <strong>Use luggage details in recommendations</strong>
                    <small>
                      When enabled, luggage size and weight influence vehicle recommendations.
                    </small>
                  </span>
                  <Toggle
                    checked={Boolean(
                      settings.bookingRules.tripPreferences.useLuggageInRecommendations
                    )}
                    onChange={(value) =>
                      updateTripPreferences("useLuggageInRecommendations", value)
                    }
                  />
                </div>

                <details className="settings-disclosure">
                  <summary>
                    <span>
                      <strong>Luggage weight limits by vehicle category</strong>
                      <small>Warning thresholds and hard booking limits</small>
                    </span>
                  </summary>
                  <div className="settings-luggage-matrix settings-luggage-matrix--weight">
                    <div className="settings-luggage-matrix__head" aria-hidden="true">
                      <span>Vehicle</span>
                      <span>Warning threshold</span>
                      <span>Hard limit</span>
                    </div>
                    {["sedan", "hatchback", "mpv", "suv", "van", "pickup", "default"].map((cat) => {
                      const entry = settings.bookingRules.tripPreferences.vehicleLuggageLimits?.[cat] || {};
                      const label = cat.charAt(0).toUpperCase() + cat.slice(1);
                      return (
                        <div key={cat} className="settings-vehicle-luggage-row settings-vehicle-luggage-row--weight">
                          <span className="settings-vehicle-luggage-row__label">{label}</span>
                          <Field label="Warning threshold (kg)" helper="Amber warning above this weight.">
                            <input
                              className="settings-input settings-input--sm"
                              type="number"
                              min="1"
                              step="1"
                              value={entry.soft ?? ""}
                              onChange={(e) => updateVehicleLuggageLimit(cat, "soft", e.target.value)}
                            />
                          </Field>
                          <Field label="Hard limit (kg)" helper="Blocks form above this weight.">
                            <input
                              className="settings-input settings-input--sm"
                              type="number"
                              min="1"
                              step="1"
                              value={entry.hard ?? ""}
                              onChange={(e) => updateVehicleLuggageLimit(cat, "hard", e.target.value)}
                            />
                          </Field>
                        </div>
                      );
                    })}
                  </div>
                </details>

                <details className="settings-disclosure">
                  <summary>
                    <span>
                      <strong>Max bag count by vehicle and luggage size</strong>
                      <small>Small through extra-large; 0 means no limit</small>
                    </span>
                  </summary>
                  <div className="settings-luggage-matrix settings-luggage-matrix--bags">
                    <div className="settings-luggage-matrix__head" aria-hidden="true">
                      <span>Vehicle</span>
                      <span>Small</span>
                      <span>Medium</span>
                      <span>Large</span>
                      <span>Extra Large</span>
                    </div>
                    {["sedan", "hatchback", "mpv", "suv", "van", "pickup", "default"].map((cat) => {
                      const entry = settings.bookingRules.tripPreferences.vehicleLuggageBagLimits?.[cat] || {};
                      const label = cat.charAt(0).toUpperCase() + cat.slice(1);
                      return (
                        <div key={cat} className="settings-vehicle-luggage-row settings-vehicle-luggage-row--bags">
                          <span className="settings-vehicle-luggage-row__label">{label}</span>
                          {[
                            { key: "small",      label: "Small" },
                            { key: "medium",     label: "Medium" },
                            { key: "large",      label: "Large" },
                            { key: "extraLarge", label: "Extra Large" },
                          ].map(({ key, label: sizeLabel }) => (
                            <Field key={key} label={`${sizeLabel} bags`} helper="0 = no limit.">
                              <input
                                className="settings-input settings-input--sm"
                                type="number"
                                min="0"
                                step="1"
                                value={entry[key] ?? ""}
                                onChange={(e) => updateVehicleLuggageBagLimit(cat, key, e.target.value)}
                              />
                            </Field>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </details>
              </div>
            </section>

            <section className="settings-card settings-extra-charges-card">
              <div className="settings-card-head">
                <h2>Extra Charges</h2>
                <p>
                  Configure delivery fee handling and travel charge policies shown during booking.
                </p>
              </div>

              <div className="settings-payment-form">
                <Field label="Delivery Fee Type">
                  <select
                    className="settings-input"
                    value={settings.bookingRules.extraCharges.deliveryFeeMode}
                    onChange={(event) => updateExtraCharges("deliveryFeeMode", event.target.value)}
                  >
                    {DELIVERY_FEE_MODE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Default Delivery Fee">
                  <input
                    className="settings-input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={settings.bookingRules.extraCharges.fixedDeliveryFee}
                    onChange={(event) => updateExtraCharges("fixedDeliveryFee", event.target.value)}
                  />
                </Field>

                <Field
                  label="Base delivery fee"
                  helper="Base fee is added once. Per-kilometer rate is multiplied by the delivery distance."
                >
                  <input
                    className="settings-input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={settings.bookingRules.extraCharges.baseDeliveryFee}
                    onChange={(event) => updateExtraCharges("baseDeliveryFee", event.target.value)}
                  />
                </Field>

                <Field
                  label="Succeeding rate per kilometer"
                  helper="Base fee is added once. Per-kilometer rate is multiplied by the delivery distance."
                >
                  <input
                    className="settings-input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={settings.bookingRules.extraCharges.perKmDeliveryFee}
                    onChange={(event) => updateExtraCharges("perKmDeliveryFee", event.target.value)}
                  />
                </Field>

                <Field label="Toll Fee Handling">
                  <select
                    className="settings-input"
                    value={settings.bookingRules.extraCharges.tollFeePolicy}
                    onChange={(event) => updateExtraCharges("tollFeePolicy", event.target.value)}
                  >
                    {EXTRA_CHARGE_POLICY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Ferry/Barge Fee Handling">
                  <select
                    className="settings-input"
                    value={settings.bookingRules.extraCharges.ferryBargeFeePolicy}
                    onChange={(event) => updateExtraCharges("ferryBargeFeePolicy", event.target.value)}
                  >
                    {EXTRA_CHARGE_POLICY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Fuel Policy">
                  <select
                    className="settings-input"
                    value={settings.bookingRules.extraCharges.fuelPolicy}
                    onChange={(event) => updateExtraCharges("fuelPolicy", event.target.value)}
                  >
                    {FUEL_POLICY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </Field>

                <div className="settings-payment-active-row">
                  <span>
                    <strong>Active</strong>
                    <small>Inactive rules fall back to FleetX review for delivery fees.</small>
                  </span>
                  <Toggle
                    checked={settings.bookingRules.extraCharges.status === "active"}
                    onChange={(value) => updateExtraCharges("status", value ? "active" : "inactive")}
                  />
                </div>

                <Field label="Other Charge Notes">
                  <textarea
                    className="settings-input settings-textarea"
                    value={settings.bookingRules.extraCharges.otherChargeNotes}
                    onChange={(event) => updateExtraCharges("otherChargeNotes", event.target.value)}
                    placeholder="Optional notes shown on invoices/contracts."
                  />
                </Field>
              </div>
            </section>

            <section className="settings-card settings-coverage-card">
              <div className="settings-card-head">
                <h2>Service Coverage & Restricted Areas</h2>
                <p>
                  Add areas FleetX should not accept for pickup, destination/drop-off,
                  or delivery bookings.
                </p>
              </div>

              <form className="settings-payment-form" onSubmit={saveRestrictedArea}>
                <Field label="Area Name">
                  <input
                    className="settings-input"
                    placeholder="Bohol, Panglao, Tagbilaran"
                    value={restrictedAreaForm.areaName}
                    onChange={(event) => updateRestrictedAreaForm("areaName", event.target.value)}
                  />
                  {restrictedAreaErrors.areaName ? (
                    <small className="settings-inline-error">{restrictedAreaErrors.areaName}</small>
                  ) : null}
                </Field>

                <Field label="Keywords" helper="Separate keywords with commas. Matching is case-insensitive.">
                  <input
                    className="settings-input"
                    placeholder="bohol, tagbilaran, panglao"
                    value={restrictedAreaForm.keywords}
                    onChange={(event) => updateRestrictedAreaForm("keywords", event.target.value)}
                  />
                  {restrictedAreaErrors.keywords ? (
                    <small className="settings-inline-error">{restrictedAreaErrors.keywords}</small>
                  ) : null}
                </Field>

                <div className="settings-restricted-targets">
                  <span className="settings-mini-label">Applies To</span>
                  {RESTRICTED_AREA_TARGET_OPTIONS.map((target) => (
                    <label key={target.value}>
                      <input
                        type="checkbox"
                        checked={target.targets.every((value) =>
                          restrictedAreaForm.appliesTo.includes(value)
                        )}
                        onChange={() => toggleRestrictedTarget(target.targets)}
                      />
                      {target.label}
                    </label>
                  ))}
                  {restrictedAreaErrors.appliesTo ? (
                    <small className="settings-inline-error">{restrictedAreaErrors.appliesTo}</small>
                  ) : null}
                </div>

                <div className="settings-payment-active-row">
                  <span>
                    <strong>Active</strong>
                    <small>Inactive rules are saved but do not block bookings.</small>
                  </span>
                  <Toggle
                    checked={restrictedAreaForm.status === "active"}
                    onChange={(value) =>
                      updateRestrictedAreaForm("status", value ? "active" : "inactive")
                    }
                  />
                </div>

                <Field label="Customer Message">
                  <textarea
                    className="settings-input settings-textarea"
                    value={restrictedAreaForm.message}
                    onChange={(event) => updateRestrictedAreaForm("message", event.target.value)}
                  />
                </Field>

                <div className="settings-payment-actions">
                  <button
                    type="submit"
                    className="settings-primary-btn"
                    disabled={restrictedAreaSaving}
                  >
                    {restrictedAreaSaving
                      ? "Saving..."
                      : restrictedAreaEditingId
                      ? "Update Rule"
                      : "Add Rule"}
                  </button>
                  {restrictedAreaEditingId ? (
                    <button
                      type="button"
                      className="settings-secondary-btn"
                      onClick={resetRestrictedAreaForm}
                      disabled={restrictedAreaSaving}
                    >
                      Cancel Edit
                    </button>
                  ) : null}
                </div>
              </form>
            </section>

            <section className="settings-card settings-payment-list-card settings-restricted-list-card">
              <div className="settings-card-head">
                <h2>Configured Restricted Areas</h2>
                <p>No default areas are seeded. Rules appear here only after an admin adds them.</p>
              </div>

              {restrictedAreaLoading ? <SettingsSkeleton /> : null}

              {!restrictedAreaLoading && restrictedAreas.length === 0 ? (
                <div className="muted">No restricted areas configured.</div>
              ) : null}

              {!restrictedAreaLoading && restrictedAreas.length ? (
                <div className="settings-restricted-list">
                  {restrictedAreas.map((rule) => (
                    <article className="settings-restricted-item" key={rule._id}>
                      <div className="settings-restricted-item__main">
                        <div>
                          <strong>{rule.areaName}</strong>
                          <small>Keywords: {(rule.keywords || []).join(", ") || "None"}</small>
                          <small>Applies to: {formatRestrictedTargets(rule.appliesTo || [])}</small>
                          <em>{rule.message || DEFAULT_RESTRICTED_AREA_MESSAGE}</em>
                        </div>
                      </div>
                      <div className="settings-payment-item__actions">
                        <span className={`badge ${rule.status === "active" ? "badge-approved" : "badge-rejected"}`}>
                          {rule.status === "active" ? "Active" : "Inactive"}
                        </span>
                        <button
                          type="button"
                          className="settings-secondary-btn"
                          onClick={() => editRestrictedArea(rule)}
                          disabled={restrictedAreaSaving}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="settings-secondary-btn"
                          onClick={() =>
                            setRestrictedAreaStatus(
                              rule,
                              rule.status === "active" ? "inactive" : "active"
                            )
                          }
                          disabled={restrictedAreaSaving}
                        >
                          {rule.status === "active" ? "Deactivate" : "Activate"}
                        </button>
                        <button
                          type="button"
                          className="settings-danger-btn"
                          onClick={() => deleteRestrictedArea(rule)}
                          disabled={restrictedAreaSaving}
                        >
                          Delete
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              ) : null}
            </section>

            <section className="settings-card settings-payment-list-card settings-penalty-rules-card">
              <div className="settings-card-head">
                <h2>Penalty & Extension Rules</h2>
                <p>
                  Configure late return penalty behavior for future return assessment.
                  Approved extensions remain penalty-free for the approved period.
                </p>
              </div>

              {penaltyRuleLoading ? <SettingsSkeleton /> : null}

              {!penaltyRuleLoading ? (
                <form className="settings-payment-form" onSubmit={savePenaltyRule}>
                  <Field label="Grace Period (hours)">
                    <input
                      className="settings-input"
                      type="number"
                      min="0"
                      step="0.25"
                      value={penaltyRuleForm.gracePeriodHours}
                      onChange={(event) =>
                        updatePenaltyRuleForm("gracePeriodHours", event.target.value)
                      }
                    />
                    {penaltyRuleErrors.gracePeriodHours ? (
                      <small className="settings-inline-error">
                        {penaltyRuleErrors.gracePeriodHours}
                      </small>
                    ) : null}
                  </Field>

                  <Field label="Penalty Type">
                    <select
                      className="settings-input"
                      value={penaltyRuleForm.penaltyType}
                      onChange={(event) =>
                        updatePenaltyRuleForm("penaltyType", event.target.value)
                      }
                    >
                      {PENALTY_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {penaltyRuleErrors.penaltyType ? (
                      <small className="settings-inline-error">
                        {penaltyRuleErrors.penaltyType}
                      </small>
                    ) : null}
                  </Field>

                  <Field label="Penalty Value">
                    <input
                      className="settings-input"
                      type="number"
                      min="0"
                      step="0.01"
                      value={penaltyRuleForm.penaltyValue}
                      onChange={(event) =>
                        updatePenaltyRuleForm("penaltyValue", event.target.value)
                      }
                    />
                    {penaltyRuleErrors.penaltyValue ? (
                      <small className="settings-inline-error">
                        {penaltyRuleErrors.penaltyValue}
                      </small>
                    ) : null}
                  </Field>

                  <div className="settings-penalty-toggles">
                    <div>
                      <span>
                        <strong>Apply only when no approved extension exists</strong>
                        <small>Approved extensions avoid late penalty for their covered period.</small>
                      </span>
                      <Toggle
                        checked={penaltyRuleForm.applyWhenNoApprovedExtension}
                        onChange={(value) =>
                          updatePenaltyRuleForm("applyWhenNoApprovedExtension", value)
                        }
                      />
                    </div>
                    <div>
                      <span>
                        <strong>Allow manual override</strong>
                        <small>Future return assessment can allow admin adjustment or waiver.</small>
                      </span>
                      <Toggle
                        checked={penaltyRuleForm.allowManualOverride}
                        onChange={(value) => updatePenaltyRuleForm("allowManualOverride", value)}
                      />
                    </div>
                    <div>
                      <span>
                        <strong>Require remarks for override</strong>
                        <small>Admin must explain waived or adjusted penalties.</small>
                      </span>
                      <Toggle
                        checked={penaltyRuleForm.requireOverrideRemarks}
                        onChange={(value) =>
                          updatePenaltyRuleForm("requireOverrideRemarks", value)
                        }
                      />
                    </div>
                    <div>
                      <span>
                        <strong>Active</strong>
                        <small>Inactive rules are saved but not used by future assessment.</small>
                      </span>
                      <Toggle
                        checked={penaltyRuleForm.status === "active"}
                        onChange={(value) =>
                          updatePenaltyRuleForm("status", value ? "active" : "inactive")
                        }
                      />
                    </div>
                  </div>

                  <div className="settings-penalty-summary">
                    <div>
                      <span>Current rule</span>
                      <strong>{formatPenaltyTypeLabel(penaltyRuleForm.penaltyType)}</strong>
                    </div>
                    <div>
                      <span>Value</span>
                      <strong>{penaltyRuleForm.penaltyValue || 0}</strong>
                    </div>
                    <div>
                      <span>Status</span>
                      <strong>{penaltyRuleForm.status === "active" ? "Active" : "Inactive"}</strong>
                    </div>
                  </div>

                  <div className="settings-payment-actions">
                    <button
                      type="submit"
                      className="settings-primary-btn"
                      disabled={penaltyRuleSaving}
                    >
                      {penaltyRuleSaving ? "Saving..." : "Save Penalty Rules"}
                    </button>
                    <button
                      type="button"
                      className="settings-secondary-btn"
                      onClick={() =>
                        setPenaltyRuleStatus(
                          penaltyRuleForm.status === "active" ? "inactive" : "active"
                        )
                      }
                      disabled={penaltyRuleSaving || !penaltyRule}
                    >
                      {penaltyRuleForm.status === "active" ? "Deactivate" : "Activate"}
                    </button>
                  </div>
                </form>
              ) : null}
            </section>
          </div>
        ) : null}

        {!loading && activeTab === "contracts" ? (
          <div className="settings-contract-grid">
            <section className="settings-card settings-contract-editor-card">
              <div className="settings-card-head">
                <h2>Contract Template</h2>
                <p>
                  Manage the active FleetX rental agreement template used in the customer
                  booking contract modal. Accepted contracts keep their own rendered snapshot.
                </p>
              </div>

              {!canManageContractTemplates ? (
                <div className="settings-access-note">
                  Only Technical Admin accounts can edit contract templates.
                </div>
              ) : null}

              <div className="settings-payment-form">
                <Field label="Contract Title">
                  <input
                    className="settings-input"
                    value={settings.contractTemplate.title}
                    disabled={!canManageContractTemplates}
                    onChange={(event) => updateContractTemplate("title", event.target.value)}
                  />
                </Field>
                <Field label="Company Name">
                  <input
                    className="settings-input"
                    value={settings.contractTemplate.companyName}
                    disabled={!canManageContractTemplates}
                    onChange={(event) => updateContractTemplate("companyName", event.target.value)}
                  />
                </Field>
                <Field label="Version Label">
                  <input
                    className="settings-input"
                    value={settings.contractTemplate.versionLabel}
                    disabled={!canManageContractTemplates}
                    onChange={(event) => updateContractTemplate("versionLabel", event.target.value)}
                    placeholder="v1.0"
                  />
                </Field>
                <Field label="Effective Date">
                  <input
                    className="settings-input"
                    type="date"
                    value={settings.contractTemplate.effectiveDate}
                    disabled={!canManageContractTemplates}
                    onChange={(event) => updateContractTemplate("effectiveDate", event.target.value)}
                  />
                </Field>
                <Field label="Intro Paragraph">
                  <textarea
                    className="settings-input settings-textarea"
                    value={settings.contractTemplate.intro}
                    disabled={!canManageContractTemplates}
                    onChange={(event) => updateContractTemplate("intro", event.target.value)}
                  />
                </Field>
                <Field label="Footer / Acknowledgement">
                  <textarea
                    className="settings-input settings-textarea"
                    value={settings.contractTemplate.footerText}
                    disabled={!canManageContractTemplates}
                    onChange={(event) => updateContractTemplate("footerText", event.target.value)}
                  />
                </Field>
              </div>

              <div className="settings-contract-sections">
                <div className="settings-contract-sections__head">
                  <span>Numbered Sections</span>
                  <button
                    type="button"
                    className="settings-secondary-btn"
                    onClick={() => {
                      setExpandedContractSection(settings.contractTemplate.sections?.length || 0);
                      addContractSection();
                    }}
                    disabled={!canManageContractTemplates}
                  >
                    Add Section
                  </button>
                </div>
                {(settings.contractTemplate.sections || []).map((section, index) => {
                  const isExpanded = expandedContractSection === index;
                  return (
                  <article
                    className={`settings-contract-section-editor ${isExpanded ? "is-expanded" : ""}`}
                    key={`${section.sortOrder}-${index}`}
                  >
                    <div className="settings-contract-section-editor__top">
                      <strong>{index + 1}</strong>
                      <input
                        className="settings-input"
                        value={section.title}
                        disabled={!canManageContractTemplates}
                        onChange={(event) =>
                          updateContractSection(index, { title: event.target.value })
                        }
                        placeholder="Section title"
                      />
                      <span className="settings-contract-section-status">
                        Active
                        <Toggle
                          checked={section.enabled !== false}
                          onChange={(value) =>
                            canManageContractTemplates &&
                            updateContractSection(index, { enabled: value })
                          }
                        />
                      </span>
                      <button
                        type="button"
                        className="settings-contract-section-toggle"
                        aria-label={`${isExpanded ? "Collapse" : "Expand"} section ${index + 1}`}
                        aria-expanded={isExpanded}
                        onClick={() => setExpandedContractSection(isExpanded ? -1 : index)}
                      >
                        <span aria-hidden="true">⌄</span>
                      </button>
                    </div>
                    <div className="settings-contract-section-editor__body" aria-hidden={!isExpanded}>
                      <textarea
                        className="settings-input settings-textarea settings-contract-section-body"
                        value={section.body}
                        disabled={!canManageContractTemplates}
                        onChange={(event) =>
                          updateContractSection(index, { body: event.target.value })
                        }
                        placeholder="Section wording. Use placeholders like {{customerName}}."
                      />
                      <div className="settings-contract-section-editor__actions">
                        <button
                          type="button"
                          className="settings-danger-btn"
                          onClick={() => removeContractSection(index)}
                          disabled={!canManageContractTemplates || settings.contractTemplate.sections.length <= 1}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </article>
                  );
                })}
              </div>

              <div className="settings-payment-actions">
                <button
                  type="button"
                  className="settings-secondary-btn"
                  onClick={resetContractTemplate}
                  disabled={!canManageContractTemplates}
                >
                  Reset to Default
                </button>
                <button
                  type="button"
                  className="settings-primary-btn"
                  onClick={saveSettings}
                  disabled={saving || !canManageContractTemplates}
                >
                  {saving ? "Saving..." : "Save Template"}
                </button>
              </div>
            </section>

            <div className="settings-contract-side">
            <aside className="settings-card settings-contract-helper-card">
              <div className="settings-card-head">
                <h2>Placeholders</h2>
                <p>These tokens are replaced with actual booking data when customers view or download the contract.</p>
              </div>
              <div className="settings-placeholder-list">
                {CONTRACT_PLACEHOLDERS.map((placeholder) => (
                  <code key={placeholder}>{`{{${placeholder}}}`}</code>
                ))}
              </div>
              <p className="settings-placeholder-aliases-note">
                Aliases also supported:{" "}
                <code>{"{{vehicle}}"}</code>,{" "}
                <code>{"{{vehicleModel}}"}</code>,{" "}
                <code>{"{{plateNo}}"}</code>,{" "}
                <code>{"{{licensePlate}}"}</code>
              </p>
            </aside>

            <section className="settings-card settings-contract-preview-card">
              {(() => {
                const preview = renderContractTemplatePreview(settings.contractTemplate);
                return (
                  <>
                    <div className="settings-card-head">
                      <h2>Preview</h2>
                      <p>Sample rendering using mock booking values.</p>
                    </div>
                    <article className="settings-contract-preview-paper">
                      <header>
                        <span>{preview.companyName}</span>
                        <h3>{preview.title}</h3>
                        <p>{preview.intro}</p>
                        <small>
                          {preview.versionLabel ? `Version ${preview.versionLabel}` : ""}
                          {preview.versionLabel && preview.effectiveDate ? " | " : ""}
                          {preview.effectiveDate ? `Effective ${preview.effectiveDate}` : ""}
                        </small>
                      </header>
                      {preview.sections.map((section, index) => (
                        <section key={`${section.title}-${index}`}>
                          <h4>{index + 1}. {section.title}</h4>
                          {contractParagraphs(section.body).map((paragraph, paragraphIndex) => (
                            <p key={paragraphIndex}>{paragraph}</p>
                          ))}
                        </section>
                      ))}
                      {preview.footerText ? (
                        <footer>
                          {contractParagraphs(preview.footerText).map((paragraph, index) => (
                            <p key={index}>{paragraph}</p>
                          ))}
                        </footer>
                      ) : null}
                    </article>
                  </>
                );
              })()}
            </section>
            </div>
          </div>
        ) : null}

        {!loading && activeTab === "general" ? (
          <div className="settings-general-grid">
            <section className="settings-card settings-brand-card">
              <div className="settings-card-head"><h2>Brand & System</h2></div>
              <div className="settings-brand-layout">
                <div className="settings-logo-block">
                  <label className="settings-logo-upload">
                    {logoPreview ? <img src={logoPreview} alt="Company logo" /> : <span>Logo</span>}
                    <input hidden type="file" accept="image/jpeg,image/jpg,image/png,image/webp" onChange={(event) => handleLogo(event.target.files?.[0])} />
                  </label>
                  <label className="settings-secondary-btn">Change Logo<input hidden type="file" accept="image/jpeg,image/jpg,image/png,image/webp" onChange={(event) => handleLogo(event.target.files?.[0])} /></label>
                  <small>PNG, JPG or WEBP. Max 2MB.</small>
                  {logoImageError ? <small className="settings-inline-error">{logoImageError}</small> : null}
                </div>
                <div className="settings-stack">
                  <Field label="System Name"><input className="settings-input" value={settings.general.systemName} onChange={(event) => updateGeneral("systemName", event.target.value)} /></Field>
                  <Field label="Support Email"><input className="settings-input" value={settings.general.email} onChange={(event) => updateGeneral("email", event.target.value)} /></Field>
                  <Field label="Contact Number"><input className="settings-input" placeholder="Enter contact number" value={settings.general.phone} onChange={(event) => updateGeneral("phone", event.target.value)} /></Field>
                  <Field label="Address"><textarea className="settings-input settings-textarea" value={settings.general.address} onChange={(event) => updateGeneral("address", event.target.value)} /></Field>
                </div>
              </div>
            </section>

            <section className="settings-card">
              <div className="settings-card-head"><h2>Footer & Contact</h2></div>
              <Field label="Footer Description"><textarea className="settings-input settings-textarea settings-textarea--footer" value={settings.general.footerDescription} onChange={(event) => updateGeneral("footerDescription", event.target.value)} /></Field>
              <Field label="Website URL"><input className="settings-input" value={settings.general.websiteUrl} onChange={(event) => updateGeneral("websiteUrl", event.target.value)} /></Field>
              <div className="settings-social-list">
                <span className="settings-mini-label">Social / Contact Links</span>
                {settings.general.socialLinks.map((link, index) => (
                  <div className="settings-social-row" key={`${link.label}-${index}`}>
                    <input className="settings-input" value={link.label} onChange={(event) => updateSocialLink(index, "label", event.target.value)} />
                    <input className="settings-input" value={link.url} onChange={(event) => updateSocialLink(index, "url", event.target.value)} />
                    <button type="button" className="settings-icon-btn" onClick={() => removeSocialLink(index)}>x</button>
                  </div>
                ))}
                <button type="button" className="settings-secondary-btn settings-add-info" onClick={addSocialLink}>+ Add Link</button>
              </div>
            </section>

            <section className="settings-card">
              <div className="settings-card-head"><h2>Platform Preferences</h2></div>
              <div className="settings-stack">
                <Field label="Currency"><select className="settings-input" value={settings.general.preferences.currency} onChange={(event) => updatePreferences("currency", event.target.value)}><option>PHP - Philippine Peso (P)</option><option>USD - US Dollar ($)</option></select></Field>
                <Field label="Timezone"><select className="settings-input" value={settings.general.preferences.timezone} onChange={(event) => updatePreferences("timezone", event.target.value)}><option>(GMT+08:00) Asia/Manila</option><option>(GMT+00:00) UTC</option></select></Field>
                <Field label="Booking Reference Prefix"><select className="settings-input" value={settings.general.preferences.bookingPrefix} onChange={(event) => updatePreferences("bookingPrefix", event.target.value)}><option>FTX-</option><option>CAPT-</option><option>BK-</option></select></Field>
                <Field label="Default Deposit Type"><select className="settings-input" value={settings.general.preferences.depositType} onChange={(event) => updatePreferences("depositType", event.target.value)}><option>Percentage (%)</option><option>Fixed Amount</option></select></Field>
                <Field label="Default Pagination"><select className="settings-input" value={settings.general.preferences.pagination} onChange={(event) => updatePreferences("pagination", event.target.value)}><option>10 per page</option><option>20 per page</option><option>50 per page</option></select></Field>
              </div>
            </section>

            <section className="settings-card settings-footer-preview-card">
              <div className="settings-card-head"><h2>Footer Preview</h2><p>This is how your footer will appear on the customer website.</p></div>
              <div className="settings-footer-preview">
                <div>
                  {logoPreview ? <img src={logoPreview} alt="Footer logo" /> : null}
                  <p>{settings.general.footerDescription}</p>
                </div>
                <div><strong>Quick Links</strong><span>Browse Vehicles</span><span>How It Works</span><span>FAQs</span><span>Contact Us</span></div>
                <div><strong>Customer Support</strong><span>{settings.general.email}</span><span>{settings.general.phone}</span><span>Mon - Sun, 8:00 AM - 8:00 PM</span></div>
                <div><strong>Follow Us</strong><div className="settings-footer-icons">{settings.general.socialLinks.slice(0, 3).map((link) => <em key={link.label}>{link.label.charAt(0).toUpperCase()}</em>)}</div></div>
                <footer><span>© 2025 {settings.general.systemName}. All rights reserved.</span><span>Terms & Conditions</span><span>Privacy Policy</span></footer>
              </div>
            </section>

            <section className="settings-card">
              <div className="settings-card-head"><h2>System Controls</h2></div>
              <div className="settings-behavior-list">
                <div><span><strong>Email Notifications</strong><small>Send email notifications for bookings and updates.</small></span><Toggle checked={settings.general.controls.emailNotifications} onChange={(value) => updateControls("emailNotifications", value)} /></div>
                <div><span><strong>OTP Verification Required</strong><small>Require OTP for customer login and sensitive actions.</small></span><Toggle checked={settings.general.controls.otpRequired} onChange={(value) => updateControls("otpRequired", value)} /></div>
                <div><span><strong>Maintenance Mode</strong><small>Temporarily disable public access to the platform.</small></span><Toggle checked={settings.general.controls.maintenanceMode} onChange={(value) => updateControls("maintenanceMode", value)} /></div>
                <div><span><strong>Session Timeout</strong><small>Automatically log out inactive accounts.</small></span><select className="settings-input" value={settings.general.controls.sessionTimeout} onChange={(event) => updateControls("sessionTimeout", event.target.value)}><option>15 minutes</option><option>30 minutes</option><option>1 hour</option></select></div>
              </div>
            </section>
          </div>
        ) : null}

        {!loading && activeTab === "payments" ? (
          <div className="settings-payment-grid">
            <section className="settings-card">
              <div className="settings-card-head">
                <h2>Payment Methods</h2>
                <p>
                  Manage the payment options and QR codes shown to customers when
                  paying for approved bookings.
                </p>
              </div>

              <form className="settings-payment-form" onSubmit={savePaymentMethod}>
                <Field label="Method Name">
                  <input
                    className="settings-input"
                    placeholder="GCash, BPI, BDO"
                    value={paymentForm.name}
                    onChange={(event) => updatePaymentForm("name", event.target.value)}
                  />
                  {paymentErrors.name ? (
                    <small className="settings-inline-error">{paymentErrors.name}</small>
                  ) : null}
                </Field>

                <Field label="Category">
                  <select
                    className="settings-input"
                    value={paymentForm.category}
                    onChange={(event) => updatePaymentForm("category", event.target.value)}
                  >
                    {PAYMENT_CATEGORIES.map((category) => (
                      <option key={category}>{category}</option>
                    ))}
                  </select>
                  {paymentErrors.category ? (
                    <small className="settings-inline-error">{paymentErrors.category}</small>
                  ) : null}
                </Field>

                <Field label="Account Name">
                  <input
                    className="settings-input"
                    value={paymentForm.accountName}
                    onChange={(event) => updatePaymentForm("accountName", event.target.value)}
                  />
                </Field>

                <Field label="Account Number">
                  <input
                    className="settings-input"
                    placeholder="Enter account or mobile number"
                    value={paymentForm.accountNumber}
                    onChange={(event) => updatePaymentForm("accountNumber", event.target.value)}
                  />
                </Field>

                <Field label="Instructions">
                  <textarea
                    className="settings-input settings-textarea"
                    value={paymentForm.instructions}
                    onChange={(event) => updatePaymentForm("instructions", event.target.value)}
                  />
                </Field>

                <div className="settings-payment-active-row">
                  <span>
                    <strong>Active</strong>
                    <small>Inactive methods are hidden from public payment options.</small>
                  </span>
                  <Toggle
                    checked={paymentForm.isActive}
                    onChange={(value) => updatePaymentForm("isActive", value)}
                  />
                </div>

                <div className="settings-payment-qr">
                  <span className="settings-mini-label">QR Code Upload</span>
                  <label className="settings-upload settings-payment-qr-upload">
                    {qrPreview ? (
                      <img src={qrPreview} alt="Payment method QR preview" />
                    ) : (
                      <strong>Upload QR Code</strong>
                    )}
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp"
                      onChange={(event) => handleQrCode(event.target.files?.[0])}
                    />
                  </label>
                  <small>JPG, PNG, or WEBP. Max 1MB.</small>
                  {paymentErrors.qrCode ? (
                    <small className="settings-inline-error">{paymentErrors.qrCode}</small>
                  ) : null}
                </div>

                <div className="settings-payment-actions">
                  <button
                    type="submit"
                    className="settings-primary-btn"
                    disabled={paymentSaving}
                  >
                    {paymentSaving
                      ? "Saving..."
                      : paymentEditingId
                      ? "Update Method"
                      : "Add Method"}
                  </button>
                  {paymentEditingId ? (
                    <button
                      type="button"
                      className="settings-secondary-btn"
                      onClick={resetPaymentForm}
                      disabled={paymentSaving}
                    >
                      Cancel Edit
                    </button>
                  ) : null}
                </div>
              </form>
            </section>

            <section className="settings-card settings-payment-list-card">
              <div className="settings-card-head">
                <h2>Configured Methods</h2>
                <p>Use separate categories for E-Wallet, Online Banking / Bank Transfer, and Bank Deposit so customers see the right proof instructions.</p>
              </div>

              {paymentLoading ? <SettingsSkeleton /> : null}

              {!paymentLoading && paymentMethods.length === 0 ? (
                <div className="muted">No payment methods configured.</div>
              ) : null}

              {!paymentLoading && paymentMethods.length ? (
                <div className="settings-payment-list">
                  {paymentMethods.map((method) => (
                    <article className="settings-payment-item" key={method._id}>
                      <div className="settings-payment-item__main">
                        {method.qrCodeUrl ? (
                          <img src={getImageSrc(method.qrCodeUrl)} alt={`${method.name} QR`} />
                        ) : (
                          <span>No QR</span>
                        )}
                        <div>
                          <strong>{method.name}</strong>
                          <small>{normalizePaymentCategoryLabel(method.category)}</small>
                          <small>{method.accountName || DEFAULT_PAYMENT_ACCOUNT_NAME}</small>
                          {method.accountNumber ? <em>{method.accountNumber}</em> : null}
                        </div>
                      </div>
                      <div className="settings-payment-item__actions">
                        <span className={`badge ${method.isActive ? "badge-approved" : "badge-rejected"}`}>
                          {method.isActive ? "Active" : "Inactive"}
                        </span>
                        <button
                          type="button"
                          className="settings-secondary-btn"
                          onClick={() => editPaymentMethod(method)}
                          disabled={paymentSaving || Boolean(deactivatePaymentModal?.submitting)}
                        >
                          Edit
                        </button>
                        {method.isActive ? (
                          <button
                            type="button"
                            className="settings-danger-btn"
                            onClick={() => openDeactivatePaymentMethod(method)}
                            disabled={paymentSaving || Boolean(deactivatePaymentModal?.submitting)}
                          >
                            Deactivate
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="settings-secondary-btn"
                            onClick={() => reactivatePaymentMethod(method)}
                            disabled={paymentSaving || Boolean(deactivatePaymentModal?.submitting)}
                          >
                            Reactivate
                          </button>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              ) : null}
            </section>
          </div>
        ) : null}

        {hasUnsavedChanges && !loading ? (
          <div className="settings-save-bar">
            <div><span>!</span><strong>You have unsaved changes</strong><small>Make sure to review and save your updates.</small></div>
            <div>
              <button type="button" className="settings-secondary-btn" onClick={resetChanges} disabled={saving}>Reset</button>
              <button type="button" className="settings-primary-btn" onClick={saveSettings} disabled={saving}>{saving ? "Saving..." : "Save Changes"}</button>
            </div>
          </div>
        ) : null}

        {toast ? <div className="settings-toast">{toast}</div> : null}

        {confirmAction ? (
          <div className="modal-overlay" onClick={() => !restrictedAreaSaving && setConfirmAction(null)}>
            <div
              className="modal-box modal-box--sm"
              role="dialog"
              aria-modal="true"
              aria-labelledby="settings-confirm-title"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="modal-header">
                <h3 id="settings-confirm-title" className="admin-section-title" style={{ margin: 0 }}>
                  {confirmAction.title}
                </h3>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setConfirmAction(null)}
                  disabled={restrictedAreaSaving}
                >
                  Close
                </button>
              </div>
              <p className="muted">{confirmAction.message}</p>
              <div className="btn-row" style={{ marginTop: 16 }}>
                <button
                  type="button"
                  className="settings-secondary-btn"
                  onClick={() => setConfirmAction(null)}
                  disabled={restrictedAreaSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={confirmAction.tone === "danger" ? "settings-danger-btn" : "settings-primary-btn"}
                  onClick={confirmAction.onConfirm}
                  disabled={restrictedAreaSaving}
                >
                  {restrictedAreaSaving ? confirmAction.loadingLabel || "Processing..." : confirmAction.confirmLabel}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {deactivatePaymentModal ? (
          <div
            className="modal-overlay"
            onClick={() => !deactivatePaymentModal.submitting && setDeactivatePaymentModal(null)}
          >
            <div
              className="modal-box settings-delete-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="deactivate-payment-method-title"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="settings-delete-modal__head">
                <div>
                  <h2 id="deactivate-payment-method-title">Deactivate this payment method?</h2>
                  <p>
                    This method will no longer appear to customers, but existing booking payment records will remain preserved.
                  </p>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setDeactivatePaymentModal(null)}
                  disabled={deactivatePaymentModal.submitting}
                >
                  Close
                </button>
              </div>

              <div className="settings-delete-summary">
                {deactivatePaymentModal.method.qrCodeUrl ? (
                  <img
                    src={getImageSrc(deactivatePaymentModal.method.qrCodeUrl)}
                    alt={`${deactivatePaymentModal.method.name} QR`}
                  />
                ) : (
                  <span>No QR</span>
                )}
                <div>
                  <strong>{deactivatePaymentModal.method.name}</strong>
                  <small>{deactivatePaymentModal.method.category}</small>
                  <small>{deactivatePaymentModal.method.accountName || DEFAULT_PAYMENT_ACCOUNT_NAME}</small>
                  {deactivatePaymentModal.method.accountNumber ? (
                    <em>{deactivatePaymentModal.method.accountNumber}</em>
                  ) : null}
                </div>
              </div>

              <div className="field-group" style={{ marginTop: 12, marginBottom: 8 }}>
                <label className="vehicle-field-label">
                  <span>Reason for deactivating</span>
                  <b>*</b>
                </label>
                <select
                  className="select"
                  value={
                    ["Account no longer used", "QR code outdated", "Payment provider unavailable", "Duplicate test method", "Administrative cleanup", "Other"].includes(deactivatePaymentModal.reason)
                      ? deactivatePaymentModal.reason
                      : deactivatePaymentModal.reason ? "Other" : ""
                  }
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val !== "Other" && val !== "") {
                      setDeactivatePaymentModal((prev) => ({ ...prev, reason: val, error: "" }));
                    } else if (val === "Other") {
                      setDeactivatePaymentModal((prev) => ({ ...prev, reason: "", error: "" }));
                    }
                  }}
                  disabled={deactivatePaymentModal.submitting}
                >
                  <option value="">Select a reason...</option>
                  <option>Account no longer used</option>
                  <option>QR code outdated</option>
                  <option>Payment provider unavailable</option>
                  <option>Duplicate test method</option>
                  <option>Administrative cleanup</option>
                  <option>Other</option>
                </select>
              </div>
              <div className="field-group" style={{ marginBottom: 4 }}>
                <textarea
                  className="input"
                  rows={2}
                  placeholder="Enter reason for deactivating this payment method."
                  value={deactivatePaymentModal.reason}
                  disabled={deactivatePaymentModal.submitting}
                  onChange={(e) =>
                    setDeactivatePaymentModal((prev) => ({ ...prev, reason: e.target.value, error: "" }))
                  }
                />
              </div>

              {deactivatePaymentModal.error ? (
                <span className="settings-inline-error">{deactivatePaymentModal.error}</span>
              ) : null}

              <div className="settings-delete-modal__actions">
                <button
                  type="button"
                  className="settings-secondary-btn"
                  onClick={() => setDeactivatePaymentModal(null)}
                  disabled={deactivatePaymentModal.submitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="settings-danger-btn"
                  onClick={confirmDeactivatePaymentMethod}
                  disabled={deactivatePaymentModal.submitting}
                >
                  {deactivatePaymentModal.submitting ? "Deactivating..." : "Deactivate Method"}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </AdminShell>
  );
}
